/**
 * Option Breakdown: three vertically stacked panels with shared grid alignment.
 * 1. Volume Breakdown (call/put bars or two lines), 2. Open Interest (call/put bars or two lines),
 * 3. Spot gamma index (same scaling as Vertical Spot Gamma vs OI) or optional gamma exposure (GEX), bars or line.
 * Uses current mode data: RTL, Legacy RTL, and Stable. When a metric is absent (e.g. no OI in legacy), shows "Missing data".
 */
import React, { useState, useMemo, useEffect, useCallback } from "react";
import { ScannerData } from "../../types/ScannerData";
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    ReferenceLine,
    Cell,
} from "recharts";
import { useLanguage } from "../../contexts/LanguageContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useMmecFx } from "../../theme/mmecFx";
import { useChartTheme } from "../../theme/chartTheme";
import { isDesignSystemOn } from "../../theme/mmecDsPreview";
import ToolState from "../mmec/ToolState";
import "./VolumeOISpotGammaChart.ds.css";
import { useDataSource } from "../../contexts/DataSourceContext";
import { usePersistentFilters } from "../../utils/persistentFilters";
import {
    getFilterOptions,
    getUnderlyingSymbol,
    sortByStrikePrice,
} from "../../utils/filterUtils";
import { expiryRowMatchesSelection } from "../../utils/expiryFilterUtils";
import { getCBOEFieldValue } from "../../utils/dataModeUtils";
import {
    computeSpotGammaGexMme,
    getUnderlyingPriceFallback,
    spotGammaToDisplayIndex,
} from "../../utils/gammaCalculations";
import { dataService } from "../../services/dataService";
import {
    readStablePrimarySessionFirst,
    readStableUnderlyingsSessionFirst,
} from "../../utils/stableTabStorage";
import { DataMode } from "../../types/DataMode";
import { useHistoricalModal } from "../../contexts/HistoricalModalContext";
import {
    mmecSimpleCloseBtnStyle,
    mmecSimpleFabStyle,
} from "../../theme/mmecSimpleMobile";
import {
    ProToolFilterSlot,
    useFilterRailSlotSurface,
} from "../mmec/ProToolFilterSlot";
import {
    buildStrikeExpiryStackRows,
    expiryLabelToStackBase,
    expiryPaletteColors,
    selectedStackExpiries,
    shortenExpiryLegend,
} from "../../utils/expiryStackChartData";

const TOGGLES_STORAGE_KEY = "VolumeOISpotGammaChart-toggles";

function num(item: Record<string, unknown>, ...keys: string[]): number {
    for (const k of keys) {
        const v = item[k];
        if (v === null || v === undefined || v === "" || v === "-") continue;
        const x =
            typeof v === "number" ? v : parseFloat(String(v).replace(/,/g, ""));
        if (Number.isFinite(x)) return x;
    }
    return 0;
}

function side(item: Record<string, unknown>): "C" | "P" {
    const r = String(
        item["Option Type"] ?? item.optionType ?? "",
    ).toUpperCase();
    return r === "C" || r === "CALL" ? "C" : "P";
}

/** Shared margin so all three panels have the same grid alignment (same plot area width and x scale). */
const CHART_MARGIN = { top: 12, right: 20, left: 52, bottom: 28 };

/** Hover highlight: small floating circle on the active point (line mode). */
const LINE_ACTIVE_DOT = { r: 5, strokeWidth: 2, stroke: "#ffffff" } as const;

/** Invisible vertices so the line remains clickable (Recharts hit-testing targets dots). */
const LINE_DOT_INVISIBLE = {
    r: 6,
    fill: "transparent",
    stroke: "transparent",
    strokeWidth: 0,
} as const;

const LINE_COL = {
    call: "var(--up)",
    put: "var(--down)",
    spotGamma: "var(--up)",
    gammaExposure: "var(--acc)",
};

const CALL_SIDE_PALETTE = [
    "#22c55e", // green
    "#3b82f6", // blue
    "#a16207", // brown/amber
    "#111827", // near black
    "#06b6d4", // cyan
    "#0f766e", // teal
    "#f59e0b", // amber
    "#4b5563", // slate
];

const PUT_SIDE_PALETTE = [
    "#ef4444", // red
    "#a855f7", // purple
    "#ec4899", // pink
    "#7c3aed", // violet
    "#be123c", // rose
    "#db2777", // fuchsia
    "#9333ea", // deep purple
    "#dc2626", // dark red
];

function sideStackColor(
    index: number,
    total: number,
    isCall: boolean,
    isDark: boolean,
): string {
    const palette = isCall ? CALL_SIDE_PALETTE : PUT_SIDE_PALETTE;
    const base = palette[index % palette.length];
    if (!isDark) return base;
    const darkPalette = isCall
        ? [
              "#4ade80",
              "#60a5fa",
              "#ca8a04",
              "#e5e7eb",
              "#22d3ee",
              "#14b8a6",
              "#fbbf24",
              "#9ca3af",
          ]
        : [
              "#f87171",
              "#c084fc",
              "#f472b6",
              "#a78bfa",
              "#fb7185",
              "#e879f9",
              "#d8b4fe",
              "#ef4444",
          ];
    return darkPalette[index % darkPalette.length];
}

function formatAxisK(value: number): string {
    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
    return String(value);
}

/** 0–1 lightness for hex, rgb(a), or hsl() — used for legend contrast on void background. */
function parseCssLightness(cssColor: string): number | null {
    const s = cssColor.trim();
    const rgb = s.match(
        /rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i,
    );
    if (rgb) {
        const r = Number(rgb[1]);
        const g = Number(rgb[2]);
        const b = Number(rgb[3]);
        return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
    }
    const hex6 = s.match(/^#([0-9a-f]{6})$/i);
    if (hex6) {
        const n = parseInt(hex6[1], 16);
        const r = (n >> 16) & 255;
        const g = (n >> 8) & 255;
        const b = n & 255;
        return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
    }
    const hsl = s.match(
        /hsl\(\s*([\d.]+)(?:deg)?\s+([\d.]+)%\s+([\d.]+)%/i,
    );
    if (hsl) return Number(hsl[3]) / 100;
    return null;
}

function legendColorForBackground(
    background: string,
    isDark: boolean,
): string {
    const lum = parseCssLightness(background);
    if (lum != null) return lum > 0.55 ? "#0f172a" : "#f1f5f9";
    return isDark ? "#f1f5f9" : "#0f172a";
}

export interface VolumeOISpotGammaChartProps {
    data: ScannerData[];
    dataMode?: DataMode;
    hideFilters?: boolean;
    /** Simple shell: chart-only surface; filters + panel toggles live in a left slide-out. */
    simpleLayout?: boolean;
}

export const VolumeOISpotGammaChart: React.FC<VolumeOISpotGammaChartProps> = ({
    data: dataProp,
    dataMode = "accumulated",
    hideFilters = false,
    simpleLayout = false,
}) => {
    const { language } = useLanguage();
    const { colors, isDarkMode } = useTheme();
    const chart = useChartTheme();
    const dsOn = isDesignSystemOn();
    const fx = useMmecFx();
    const { dataSource } = useDataSource();
    const { openHistorical } = useHistoricalModal();
    const {
        selectedUnderlying,
        selectedExpiry: expiry,
        selectedExpiries,
        selectedSide: sideFilter,
        setSelectedUnderlying,
        setSelectedExpiry: setExpiry,
        setSelectedExpiries,
        setSelectedSide: setSideFilter,
        setFilterState,
    } = usePersistentFilters(
        "VolumeOISpotGammaChart",
        dataMode === "5min" ? "5min" : "accumulated",
    );

    const [toggles, setToggles] = useState(() => {
        try {
            const s =
                typeof localStorage !== "undefined"
                    ? localStorage.getItem(TOGGLES_STORAGE_KEY)
                    : null;
            if (s) {
                const p = JSON.parse(s) as {
                    showVolume?: boolean;
                    showOpenInterest?: boolean;
                    showSpotGamma?: boolean;
                    expandChart?: boolean;
                    linear?: boolean;
                    showGammaExposure?: boolean;
                    showVolumeGammaExposure?: boolean;
                    mergeVolumeGexWithOI?: boolean;
                    sideBySideCallsPuts?: boolean;
                    stackEachSide?: boolean;
                    stackAllSides?: boolean;
                    mergeVolumeOI?: boolean;
                };
                return {
                    showVolume: p.showVolume ?? true,
                    showOpenInterest: p.showOpenInterest ?? true,
                    showSpotGamma: p.showSpotGamma ?? true,
                    expandChart: p.expandChart ?? false,
                    linear: p.linear ?? false,
                    showGammaExposure: p.showGammaExposure ?? false,
                    showVolumeGammaExposure: p.showVolumeGammaExposure ?? false,
                    mergeVolumeGexWithOI: p.mergeVolumeGexWithOI ?? false,
                    sideBySideCallsPuts: p.sideBySideCallsPuts ?? false,
                    stackEachSide: p.stackEachSide ?? true,
                    stackAllSides: p.stackAllSides ?? false,
                    mergeVolumeOI: p.mergeVolumeOI ?? false,
                };
            }
        } catch (_) {}
        return {
            showVolume: true,
            showOpenInterest: true,
            showSpotGamma: true,
            expandChart: false,
            linear: false,
            showGammaExposure: false,
            showVolumeGammaExposure: false,
            mergeVolumeGexWithOI: false,
            sideBySideCallsPuts: false,
            stackEachSide: true,
            stackAllSides: false,
            mergeVolumeOI: false,
        };
    });
    const setShowVolume = useCallback(
        (v: boolean) => setToggles((t) => ({ ...t, showVolume: v })),
        [],
    );
    const setShowOpenInterest = useCallback(
        (v: boolean) => setToggles((t) => ({ ...t, showOpenInterest: v })),
        [],
    );
    const setShowSpotGamma = useCallback(
        (v: boolean) => setToggles((t) => ({ ...t, showSpotGamma: v })),
        [],
    );
    const setExpandChart = useCallback(
        (v: boolean) => setToggles((t) => ({ ...t, expandChart: v })),
        [],
    );
    const setLinear = useCallback(
        (v: boolean) => setToggles((t) => ({ ...t, linear: v })),
        [],
    );
    const setShowGammaExposure = useCallback(
        (v: boolean) => setToggles((t) => ({ ...t, showGammaExposure: v })),
        [],
    );
    const setShowVolumeGammaExposure = useCallback(
        (v: boolean) => setToggles((t) => ({ ...t, showVolumeGammaExposure: v })),
        [],
    );
    const setMergeVolumeGexWithOI = useCallback(
        (v: boolean) => setToggles((t) => ({ ...t, mergeVolumeGexWithOI: v })),
        [],
    );
    const setSideBySideCallsPuts = useCallback(
        (v: boolean) => setToggles((t) => ({ ...t, sideBySideCallsPuts: v })),
        [],
    );
    const setStackEachSide = useCallback(
        (v: boolean) => setToggles((t) => ({ ...t, stackEachSide: v })),
        [],
    );
    const setStackAllSides = useCallback(
        (v: boolean) => setToggles((t) => ({ ...t, stackAllSides: v })),
        [],
    );
    const setMergeVolumeOI = useCallback(
        (v: boolean) => setToggles((t) => ({ ...t, mergeVolumeOI: v })),
        [],
    );
    useEffect(() => {
        try {
            localStorage.setItem(TOGGLES_STORAGE_KEY, JSON.stringify(toggles));
        } catch (_) {}
    }, [toggles]);

    const [simpleDrawerOpen, setSimpleDrawerOpen] = useState(false);

    useEffect(() => {
        if (!simpleLayout || !simpleDrawerOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setSimpleDrawerOpen(false);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [simpleLayout, simpleDrawerOpen]);

    const [cboeData, setCboeData] = useState<ScannerData[]>([]);
    const [underlyings, setUnderlyings] = useState<string[]>(() => {
        const list = readStableUnderlyingsSessionFirst();
        return list.length > 0 ? list : ["SPY"];
    });

    const [fetchKey, setFetchKey] = useState(0);
    const hasLoadedOnceRef = React.useRef(false);

    // Use table data for RTL / Legacy RTL / Stream so switching mode shows current data; Stable uses table data when available else fetch
    const useTableData = dataSource === "rtl_legacy";
    const chartData = useMemo(() => {
        if (useTableData) return (dataProp ?? []).filter(Boolean);
        if (dataSource === "stable" && dataProp?.length) return dataProp;
        return cboeData ?? [];
    }, [useTableData, dataSource, dataProp, cboeData]);

    useEffect(() => {
        if (dataSource !== "stable") {
            setLoading(false);
            setLoadFailed(false);
            return;
        }
        if (dataProp?.length) {
            setLoading(false);
            setLoadFailed(false);
            return;
        }
        let cancelled = false;
        if (!hasLoadedOnceRef.current) setLoading(true);
        setLoadFailed(false);
        const load = async () => {
            try {
                const symbols = underlyings.length > 0 ? underlyings : ["SPY"];
                const expiryRange =
                    localStorage.getItem("mmec_expiry_range") || "weekly";
                const data = await dataService.fetchCboeGreeksData(symbols, {
                    expiryRange,
                });
                if (!cancelled) {
                    setCboeData(data ?? []);
                    setLoadFailed(!data?.length);
                    hasLoadedOnceRef.current = true;
                }
            } catch (_) {
                if (!cancelled) {
                    setCboeData([]);
                    setLoadFailed(true);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [dataSource, dataProp?.length, underlyings.join(","), fetchKey]);

    useEffect(() => {
        if (dataSource !== "stable") return;
        const id = setInterval(() => setFetchKey((k) => k + 1), 30000);
        return () => clearInterval(id);
    }, [dataSource]);

    useEffect(() => {
        const onRefreshed = () => setFetchKey((k) => k + 1);
        window.addEventListener("stableDataFetched", onRefreshed);
        return () =>
            window.removeEventListener("stableDataFetched", onRefreshed);
    }, []);

    useEffect(() => {
        const handler = () => {
            const sym = readStablePrimarySessionFirst();
            if (sym) setUnderlyings([sym]);
            setFetchKey((k) => k + 1);
        };
        const strikeExpiryHandler = () => setFetchKey((k) => k + 1);
        window.addEventListener("stableDataFetchRequested", handler);
        window.addEventListener(
            "stableStrikeOrExpiryChanged",
            strikeExpiryHandler,
        );
        return () => {
            window.removeEventListener("stableDataFetchRequested", handler);
            window.removeEventListener(
                "stableStrikeOrExpiryChanged",
                strikeExpiryHandler,
            );
        };
    }, []);

    const filterOpts = useMemo(() => getFilterOptions(chartData), [chartData]);
    const expiries = filterOpts.availableExpiries ?? [];
    const fromDataUnderlyings = filterOpts.availableUnderlyings ?? [];
    const availableUnderlyings =
        fromDataUnderlyings.length > 0 ? fromDataUnderlyings : underlyings;
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);

    const primaryExpiryForModals = useMemo(() => {
        if (!selectedExpiries?.length || selectedExpiries.includes("all"))
            return undefined;
        const digits = (s: string) => String(s).replace(/\D/g, "").slice(0, 8);
        const sorted = [...selectedExpiries]
            .filter((e) => e !== "all")
            .sort((a, b) => digits(a).localeCompare(digits(b)));
        return sorted[0];
    }, [selectedExpiries]);

    const prevExpiriesKeyRef = React.useRef("");
    useEffect(() => {
        const key = expiries.slice().sort().join("\0");
        if (prevExpiriesKeyRef.current === key) return;
        prevExpiriesKeyRef.current = key;
        if (!expiries.length) return;
        if (!selectedExpiries.includes("all")) {
            const bad = selectedExpiries.filter((e) => !expiries.includes(e));
            if (bad.length) {
                setFilterState((prev) => {
                    const kept = selectedExpiries.filter((e) =>
                        expiries.includes(e),
                    );
                    const next = kept.length ? kept : ["all"];
                    const digits = (s: string) =>
                        String(s).replace(/\D/g, "").slice(0, 8);
                    const sorted = next
                        .filter((x) => x !== "all")
                        .sort((a, b) => digits(a).localeCompare(digits(b)));
                    const finalList = sorted.length ? sorted : ["all"];
                    const primary =
                        finalList[0] === "all" ? "all" : finalList[0];
                    return {
                        ...prev,
                        selectedExpiries: finalList,
                        selectedExpiry: primary,
                    };
                });
            }
        } else if (expiry !== "all") {
            setExpiry("all");
        }
    }, [expiries, selectedExpiries, expiry, setFilterState, setExpiry]);

    const filtered = useMemo(() => {
        if (!chartData?.length) return [];
        return chartData.filter((row) => {
            const underlying = getUnderlyingSymbol(row);
            const matchU =
                selectedUnderlying === "all" ||
                !selectedUnderlying ||
                underlying === selectedUnderlying;
            const matchE = expiryRowMatchesSelection(
                row["Expiry Date"],
                selectedExpiries,
            );
            const s = side(row as unknown as Record<string, unknown>);
            const matchS =
                sideFilter === "all" ||
                (sideFilter === "C" && s === "C") ||
                (sideFilter === "P" && s === "P");
            return matchU && matchE && matchS;
        });
    }, [chartData, selectedUnderlying, selectedExpiries, sideFilter]);

    const underlyingPriceFallback = useMemo(
        () => getUnderlyingPriceFallback(filtered, dataMode),
        [filtered, dataMode],
    );

    const byStrike = useMemo(() => {
        const map = new Map<
            number,
            {
                strike: number;
                volume: number;
                openInterest: number;
                spotGamma: number;
                gammaExposure: number;
                volumeGammaExposure: number;
                callVol: number;
                putVol: number;
                callOI: number;
                putOI: number;
                callSpotGamma: number;
                putSpotGamma: number;
            }
        >();
        for (const row of filtered) {
            const r = row as unknown as Record<string, unknown>;
            const st = num(r, "Strike", "Strike Price") || 0;
            if (!st) continue;
            // Use the SAME Volume field as the data table — no fallback to Bid/Ask accumulated.
            // Bid Volume accumelated + Ask Volume accumelated is a different metric (trade-side classified volume),
            // not the session volume. Substituting it caused mismatches vs the data table.
            const vol = Number(getCBOEFieldValue(row, "Volume")) || 0;
            const oi = getCBOEFieldValue(row, "Open Interest");
            const { spotGamma: spotGammaValue, gex: gexValue, volumeGex: volumeGexValue } =
                computeSpotGammaGexMme(row, dataMode, underlyingPriceFallback);
            const isCall = side(r) === "C";

            const cur = map.get(st);
            if (cur) {
                cur.volume += vol;
                cur.openInterest += oi;
                cur.spotGamma += spotGammaValue;
                cur.gammaExposure += gexValue;
                cur.volumeGammaExposure += volumeGexValue;
                if (isCall) {
                    cur.callVol += vol;
                    cur.callOI += oi;
                    cur.callSpotGamma += spotGammaValue;
                } else {
                    cur.putVol += vol;
                    cur.putOI += oi;
                    cur.putSpotGamma += spotGammaValue;
                }
            } else {
                map.set(st, {
                    strike: st,
                    volume: vol,
                    openInterest: oi,
                    spotGamma: spotGammaValue,
                    gammaExposure: gexValue,
                    volumeGammaExposure: volumeGexValue,
                    callVol: isCall ? vol : 0,
                    putVol: isCall ? 0 : vol,
                    callOI: isCall ? oi : 0,
                    putOI: isCall ? 0 : oi,
                    callSpotGamma: isCall ? spotGammaValue : 0,
                    putSpotGamma: isCall ? 0 : spotGammaValue,
                });
            }
        }
        const list = Array.from(map.values()).map((x) => ({
            ...x,
            strike: Number(x.strike),
        }));
        const S = underlyingPriceFallback > 0 ? underlyingPriceFallback : 100;
        return sortByStrikePrice(
            list.map((x) => ({
                ...x,
                spotGammaDisplay: spotGammaToDisplayIndex(x.spotGamma, S),
            })),
        );
    }, [filtered, dataMode, underlyingPriceFallback]);

    const stackExpiryList = useMemo(
        () => selectedStackExpiries(selectedExpiries),
        [selectedExpiries],
    );
    const stackPalette = useMemo(
        () => expiryPaletteColors(stackExpiryList?.length ?? 0, isDarkMode),
        [stackExpiryList, isDarkMode],
    );
    const stackBases = useMemo(
        () => (stackExpiryList ?? []).map(expiryLabelToStackBase),
        [stackExpiryList],
    );

    const byStrikeStacked = useMemo(() => {
        if (!stackExpiryList?.length) return null;
        return buildStrikeExpiryStackRows(
            filtered,
            stackExpiryList,
            dataMode,
            underlyingPriceFallback,
            sideFilter,
        );
    }, [
        filtered,
        stackExpiryList,
        dataMode,
        underlyingPriceFallback,
        sideFilter,
    ]);

    const chartRows =
        stackExpiryList && byStrikeStacked?.length ? byStrikeStacked : byStrike;
    const strikeCount = chartRows.length;

    const hasVolumeData = useMemo(() => {
        if (stackExpiryList && byStrikeStacked?.length && stackBases.length) {
            return byStrikeStacked.some((row) =>
                stackBases.some(
                    (b) =>
                        Math.abs(Number(row[`callVol_${b}`] ?? 0)) > 1e-9 ||
                        Math.abs(Number(row[`putVol_${b}`] ?? 0)) > 1e-9,
                ),
            );
        }
        return byStrike.some(
            (b) =>
                (b.volume ?? 0) !== 0 ||
                (b.callVol ?? 0) !== 0 ||
                (b.putVol ?? 0) !== 0,
        );
    }, [stackExpiryList, byStrikeStacked, stackBases, byStrike]);

    const hasOIData = useMemo(() => {
        if (stackExpiryList && byStrikeStacked?.length && stackBases.length) {
            return byStrikeStacked.some((row) =>
                stackBases.some(
                    (b) =>
                        Math.abs(Number(row[`callOI_${b}`] ?? 0)) > 1e-9 ||
                        Math.abs(Number(row[`putOI_${b}`] ?? 0)) > 1e-9,
                ),
            );
        }
        return byStrike.some(
            (b) =>
                (b.openInterest ?? 0) !== 0 ||
                (b.callOI ?? 0) !== 0 ||
                (b.putOI ?? 0) !== 0,
        );
    }, [stackExpiryList, byStrikeStacked, stackBases, byStrike]);

    /** Raw dollar spot gamma before index — same presence test as Vertical chart. */
    const hasSpotGammaMetricData = useMemo(() => {
        if (stackExpiryList && byStrikeStacked?.length && stackBases.length) {
            return byStrikeStacked.some((row) =>
                stackBases.some(
                    (b) => Math.abs(Number(row[`sgDisp_${b}`] ?? 0)) > 1e-9,
                ),
            );
        }
        return byStrike.some((b) => (b.spotGamma ?? 0) !== 0);
    }, [stackExpiryList, byStrikeStacked, stackBases, byStrike]);

    const hasGammaExposureMetricData = useMemo(() => {
        if (stackExpiryList && byStrikeStacked?.length && stackBases.length) {
            return byStrikeStacked.some((row) =>
                stackBases.some(
                    (b) => Math.abs(Number(row[`gex_${b}`] ?? 0)) > 1e-9,
                ),
            );
        }
        return byStrike.some((b) => (b.gammaExposure ?? 0) !== 0);
    }, [stackExpiryList, byStrikeStacked, stackBases, byStrike]);

    const hasVolumeGammaExposureMetricData = useMemo(() => {
        if (stackExpiryList && byStrikeStacked?.length && stackBases.length) {
            return byStrikeStacked.some((row) =>
                stackBases.some(
                    (b) => Math.abs(Number(row[`volGex_${b}`] ?? 0)) > 1e-9,
                ),
            );
        }
        return byStrike.some((b) => (b.volumeGammaExposure ?? 0) !== 0);
    }, [stackExpiryList, byStrikeStacked, stackBases, byStrike]);
    const noVolumeLabel =
        language === "ar"
            ? "لا يوجد حجم تداول حالياً"
            : "No trading volume recorded";
    const missingDataLabel =
        language === "ar" ? "بيانات مفقودة" : "Missing data";

    const symbol = useMemo(() => {
        if (!filtered.length) return "";
        const s = getUnderlyingSymbol(filtered[0]);
        return s ? `$${s}` : "";
    }, [filtered]);

    const effectiveUnderlying =
        selectedUnderlying && selectedUnderlying !== "all"
            ? selectedUnderlying
            : symbol
              ? symbol.replace(/^\$/, "")
              : "SPY";
    const effectiveExpiry = primaryExpiryForModals ?? (expiries[0] || "");
    const handleStrikeClick = (
        payload: { strike?: number },
        optionType: "CALL" | "PUT" = "CALL",
    ) => {
        if (payload?.strike == null) return;
        openHistorical(
            {
                underlying: effectiveUnderlying,
                strike: payload.strike,
                expiry: effectiveExpiry,
                optionType,
                source: "spot_gamma",
            },
            simpleLayout ? { compactChrome: true } : undefined,
        );
    };
    const onBarClick = (data: unknown, optionType: "CALL" | "PUT" = "CALL") => {
        const payload =
            (data as { payload?: { strike?: number } })?.payload ??
            (data as { strike?: number });
        handleStrikeClick(payload, optionType);
    };

    const strokeClr = chart.text;
    const gridClr = chart.grid;
    const axisTick = { fill: strokeClr, fontSize: 11, fontFamily: "var(--font-mono)" };
    const barTickProps = { tick: axisTick };
    const panelHeight = simpleLayout
        ? typeof window !== "undefined"
            ? Math.min(
                  200,
                  Math.max(130, Math.floor((window.innerHeight - 220) / 4)),
              )
            : 150
        : toggles.expandChart
          ? typeof window !== "undefined"
              ? Math.max(520, Math.floor((window.innerHeight - 260) * 0.42))
              : 520
          : 240;
    const {
        showVolume,
        showOpenInterest,
        showSpotGamma,
        expandChart,
        linear: linearChart,
        showGammaExposure,
        showVolumeGammaExposure,
        mergeVolumeGexWithOI,
        sideBySideCallsPuts,
        stackEachSide,
        stackAllSides,
        mergeVolumeOI,
    } = toggles;

    // Add mergedVolumeGex field when merge toggle is on (volume gamma + OI gamma combined)
    const chartRowsWithMerge = useMemo(() => {
        if (!mergeVolumeGexWithOI) return chartRows;
        return chartRows.map((row) => ({
            ...row,
            mergedVolumeGex: ((row as unknown as { volumeGammaExposure?: number }).volumeGammaExposure ?? 0) + ((row as unknown as { gammaExposure?: number }).gammaExposure ?? 0),
        }));
    }, [chartRows, mergeVolumeGexWithOI]);

    const thirdPanelDataKey = showGammaExposure
        ? "gammaExposure"
        : "spotGammaDisplay";
    const thirdPanelHasData = showGammaExposure
        ? hasGammaExposureMetricData
        : hasSpotGammaMetricData;
    const thirdPanelTitle = showGammaExposure
        ? language === "ar"
            ? "تعرض جاما"
            : "Gamma exposure"
        : language === "ar"
          ? "سخيار بيع جاما"
          : "Spot gamma";
    const thirdPanelSeriesLabel = showGammaExposure
        ? language === "ar"
            ? "تعرض جاما"
            : "Gamma exposure"
        : language === "ar"
          ? "سخيار بيع جاما (مؤشر)"
          : "Spot gamma (index)";
    /** Recharts SVG ignores CSS vars — use explicit hex (white spot gamma on dark charts).
     *  Design System preview: spot gamma / GEX series = --series-gex, direction = --up / --down. */
    const dsSuccess = dsOn ? "#26a69a" : colors.chartSuccess;
    const dsError = dsOn ? "#ef5350" : colors.chartError;
    const dsAccent = dsOn ? "#7986cb" : colors.accent;
    const spotLineClr = dsOn ? "#b2b5be" : gridClr;
    const spotGammaLineColor = dsOn
        ? "#7986cb"
        : isDarkMode
        ? "#ffffff"
        : colors.chartSuccess || "#22c55e";
    const gammaExposureLineColor = dsAccent;
    const thirdPanelStroke = showGammaExposure
        ? gammaExposureLineColor
        : spotGammaLineColor;
    const spotGammaBarPositive = spotGammaLineColor;
    const spotGammaStackFill = (index: number) =>
        showGammaExposure
            ? stackPalette[index]
            : isDarkMode
              ? spotGammaLineColor
              : stackPalette[index];
    const formatThirdPanelY = (v: number) => {
        const n = Number(v);
        if (showGammaExposure) {
            return n >= 1000 || n <= -1000
                ? `${(n / 1000).toFixed(1)}K`
                : String(Math.round(n));
        }
        return Number(v).toLocaleString(undefined, {
            maximumFractionDigits: 1,
        });
    };

    const onLinePointClick = useCallback(
        (optionType: "CALL" | "PUT") => (props: unknown) => {
            const p = (props as { payload?: { strike?: number } })?.payload;
            if (p?.strike == null) return;
            handleStrikeClick(p, optionType);
        },
        [handleStrikeClick],
    );

    const MIN_PX_PER_BAR = 10;
    const containerWidth =
        typeof window !== "undefined"
            ? window.innerWidth - (simpleLayout ? 72 : 120)
            : simpleLayout
              ? 360
              : 900;
    const needsScroll = strikeCount * MIN_PX_PER_BAR > containerWidth;
    const scrollWidth = needsScroll ? strikeCount * MIN_PX_PER_BAR : undefined;
    const xTickInterval =
        strikeCount > 80
            ? Math.ceil(strikeCount / 40) - 1
            : strikeCount > 40
              ? 1
              : 0;

    /** Shared X-axis for linear charts so strike scale and reference lines match across panels. */
    const linearXAxisProps = useMemo(
        () => ({
            type: "number" as const,
            dataKey: "strike" as const,
            domain: ["dataMin", "dataMax"] as [string, string],
            stroke: strokeClr,
            tick: { fill: strokeClr, fontSize: 11, fontFamily: "var(--font-mono)" },
            fontSize: 11,
            tickLine: false,
            axisLine: false,
            angle: -45,
            textAnchor: "end" as const,
            height: 48,
            interval: xTickInterval,
        }),
        [strokeClr, xTickInterval],
    );

    const tooltipContentStyle = {
        background: "var(--elev)",
        border: "1px solid var(--border)",
        borderRadius: 8,
        fontSize: 12,
        fontFamily: "var(--font-ui)",
        boxShadow: "var(--lift-2)",
        color: "var(--txt)",
    };

    const useExpiryStackCharts = Boolean(
        stackExpiryList?.length && byStrikeStacked?.length,
    );

    /** Page void background — not card surface (surface can be light while chart area is dark). */
    const legendTextColor = useMemo(
        () =>
            dsOn
                ? "#b2b5be"
                : legendColorForBackground(
                      colors.background || colors.surface || "",
                      isDarkMode,
                  ),
        [colors.background, colors.surface, isDarkMode, dsOn],
    );

    const legendWrapperStyle = useMemo(
        () => ({
            color: legendTextColor,
            fontSize: 11,
        }),
        [legendTextColor],
    );

    const chartScopeClass = "mmec-vol-oi-sg-charts";

    const legendCss = useMemo(
        () => `
        .${chartScopeClass},
        .${chartScopeClass} .recharts-wrapper,
        .${chartScopeClass} .recharts-surface,
        .${chartScopeClass} .recharts-legend-wrapper,
        .${chartScopeClass} .recharts-default-legend,
        .${chartScopeClass} .recharts-legend-item-text,
        .${chartScopeClass} .recharts-legend-wrapper text,
        .${chartScopeClass} .recharts-legend-wrapper span,
        .${chartScopeClass} .recharts-legend-wrapper li {
          color: ${legendTextColor} !important;
        }
        .${chartScopeClass} .recharts-legend-item-text,
        .${chartScopeClass} .recharts-legend-wrapper text,
        .${chartScopeClass} .recharts-default-legend text {
          fill: ${legendTextColor} !important;
        }
      `,
        [legendTextColor],
    );

    const CustomLegend = useMemo(() => {
        const textColor = legendTextColor;
        return (props: { payload?: readonly { value?: string; color?: string }[] }) => {
            const { payload } = props;
            if (!payload?.length) return null;
            return (
                <ul
                    className="mmec-vol-oi-sg-legend"
                    style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "4px 14px",
                        padding: 0,
                        margin: "2px 0 0 0",
                        listStyle: "none",
                        justifyContent: "center",
                        color: textColor,
                    }}
                >
                    {payload.map((entry, i) => {
                        const label = String(entry.value ?? "");
                        const isSpotGammaLegend =
                            /spot gamma|سخيار بيع جاما/i.test(label) &&
                            !/gamma exposure|تعرض جاما/i.test(label);
                        const swatch =
                            isSpotGammaLegend &&
                            entry.color &&
                            (String(entry.color).startsWith("var(") ||
                                String(entry.color) === "#000" ||
                                String(entry.color) === "#000000" ||
                                String(entry.color) === "black")
                                ? spotGammaLineColor
                                : isSpotGammaLegend
                                  ? spotGammaLineColor
                                  : entry.color &&
                                      !String(entry.color).startsWith(
                                          "var(",
                                      )
                                    ? entry.color
                                    : dsSuccess;
                        return (
                        <li
                            key={`${entry.value ?? i}-${i}`}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 5,
                                color: textColor,
                            }}
                        >
                            <span
                                style={{
                                    width: 10,
                                    height: 10,
                                    backgroundColor: swatch,
                                    display: "inline-block",
                                    borderRadius: 2,
                                    flexShrink: 0,
                                }}
                            />
                            <span
                                className="mmec-vol-oi-sg-legend-label"
                                style={{
                                    color: textColor,
                                    fontSize: 11,
                                    lineHeight: 1.2,
                                }}
                            >
                                {entry.value}
                            </span>
                        </li>
                        );
                    })}
                </ul>
            );
        };
    }, [legendTextColor, dsSuccess, spotGammaLineColor]);

    const stackExpiryTooltip = useCallback(
        ({
            active,
            payload,
            label,
        }: {
            active?: boolean;
            payload?: Array<{
                dataKey?: string;
                value?: unknown;
                color?: string;
                name?: string;
            }>;
            label?: string | number;
        }) => {
            if (!active || !payload?.length) return null;
            const rows = payload.filter(
                (p) => p.value != null && Math.abs(Number(p.value)) > 1e-9,
            );
            if (!rows.length) return null;
            return (
                <div
                    style={{
                        ...tooltipContentStyle,
                        padding: "8px 10px",
                        borderRadius: 8,
                    }}
                    className="text-xs shadow-lg"
                >
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>
                        {language === "ar"
                            ? `سعر التنفيذ ${label}`
                            : `Strike ${label}`}
                    </div>
                    {rows.map((p) => (
                        <div
                            key={String(p.dataKey)}
                            style={{
                                color: p.color ?? colors.text,
                                marginBottom: 2,
                            }}
                        >
                            {p.name}:{" "}
                            {Number(p.value).toLocaleString(undefined, {
                                maximumFractionDigits: 2,
                            })}
                        </div>
                    ))}
                </div>
            );
        },
        [colors.text, language, tooltipContentStyle],
    );

    /** Tailwind lg:/sm: breakpoints follow viewport width — the filter rail stays narrow on wide screens, so stack when portaled to the rail. */
    const inFilterRail = useFilterRailSlotSurface();

    const shellStyle: React.CSSProperties = simpleLayout
        ? {
              flex: 1,
              minHeight: 0,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              position: "relative",
              backgroundColor: colors.surface,
              color: legendTextColor,
              border: "none",
              borderRadius: 0,
          }
        : {
              minHeight: expandChart ? "calc(100vh - 160px)" : 400,
              display: "flex",
              flexDirection: "column",
              backgroundColor: "var(--elev)",
              color: legendTextColor,
              border: "1px solid var(--hair)",
              borderRadius: 10,
          };

    const filterBlock = !hideFilters ? (
        <section
            className="shrink-0 p-3 w-full min-w-0"
            style={{ borderBottom: "1px solid var(--hair)" }}
        >
            <p className="mb-2" style={{ fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".08em", color: "var(--muted-2)" }}>
                {language === "ar" ? "الأصل والنوع" : "Underlying & side"}
            </p>
            <div
                className={
                    inFilterRail
                        ? "grid grid-cols-1 gap-3"
                        : "grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-3xl"
                }
            >
                <div className="min-w-0">
                    <label
                        className="block mb-1"
                        style={{ color: "var(--muted)", fontSize: 11.5, fontWeight: 500 }}
                    >
                        {language === "ar" ? "الأصل" : "Underlying"}
                    </label>
                    <select
                        value={selectedUnderlying}
                        onChange={(e) => setSelectedUnderlying(e.target.value)}
                        className="w-full"
                        style={fx.input}
                    >
                        <option value="all">
                            {language === "ar" ? "الكل" : "All"}
                        </option>
                        {availableUnderlyings.map((u) => (
                            <option key={u} value={u}>
                                {u}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="min-w-0">
                    <label
                        className="block mb-1"
                        style={{ color: "var(--muted)", fontSize: 11.5, fontWeight: 500 }}
                    >
                        {language === "ar" ? "نوع" : "Side"}
                    </label>
                    <select
                        value={sideFilter}
                        onChange={(e) => setSideFilter(e.target.value)}
                        className="w-full"
                        style={fx.input}
                    >
                        <option value="all">
                            {language === "ar" ? "الكل" : "All"}
                        </option>
                        <option value="C">Call</option>
                        <option value="P">Put</option>
                    </select>
                </div>
            </div>

            <div
                className="mt-4 pt-4 w-full min-w-0 min-h-0 flex flex-col"
                style={{ borderTop: "1px solid var(--hair)" }}
            >
                <label
                    className="block mb-1"
                    style={{ color: "var(--muted)", fontSize: 11.5, fontWeight: 500 }}
                >
                    {language === "ar"
                        ? "تواريخ الانتهاء (متعددة)"
                        : "Expiry (multi-select)"}
                </label>
                <div
                    className="w-full flex-1 min-h-0"
                    style={{
                        ...fx.input,
                        fontSize: 12.5,
                        padding: "8px 10px",
                        maxHeight: inFilterRail
                            ? "min(52vh, 380px)"
                            : "min(40vh, 320px)",
                        minHeight: inFilterRail ? 120 : 140,
                        overflowY: "auto",
                        overflowX: "hidden",
                        boxSizing: "border-box",
                    }}
                >
                    <label
                        className="flex items-center gap-2 cursor-pointer mb-1.5"
                        style={{ color: "var(--txt)" }}
                    >
                        <input
                            type="checkbox"
                            checked={selectedExpiries.includes("all")}
                            onChange={() => setSelectedExpiries(["all"])}
                        />
                        <span>
                            {language === "ar" ? "كل التواريخ" : "All expiries"}
                        </span>
                    </label>
                    {expiries.map((exp) => (
                        <label
                            key={exp}
                            className="flex items-center gap-2 cursor-pointer mb-1"
                            style={{ color: "var(--txt)", fontSize: 12, fontFamily: "var(--font-mono)" }}
                        >
                            <input
                                type="checkbox"
                                checked={
                                    !selectedExpiries.includes("all") &&
                                    selectedExpiries.includes(exp)
                                }
                                onChange={() => {
                                    if (selectedExpiries.includes("all")) {
                                        setSelectedExpiries([exp]);
                                        return;
                                    }
                                    const has = selectedExpiries.includes(exp);
                                    const next = has
                                        ? selectedExpiries.filter(
                                              (e) => e !== exp,
                                          )
                                        : [...selectedExpiries, exp];
                                    setSelectedExpiries(
                                        next.length ? next : ["all"],
                                    );
                                }}
                            />
                            {stackExpiryList?.includes(exp) ? (
                                <span
                                    className="rounded-full shrink-0"
                                    style={{
                                        width: 8,
                                        height: 8,
                                        backgroundColor:
                                            stackPalette[
                                                Math.max(
                                                    0,
                                                    stackExpiryList.indexOf(
                                                        exp,
                                                    ),
                                                )
                                            ],
                                        flexShrink: 0,
                                    }}
                                    title={
                                        language === "ar"
                                            ? "لون هذا التاريخ على الرسم البياني"
                                            : "Chart color for this expiry"
                                    }
                                    aria-hidden
                                />
                            ) : null}
                            <span className="break-all">{exp}</span>
                        </label>
                    ))}
                </div>
            </div>
        </section>
    ) : null;

    const togglesBlock = (
        <section
            className={`shrink-0 gap-x-4 gap-y-2 items-start ${
                simpleLayout
                    ? "flex flex-col p-3 items-stretch"
                    : inFilterRail
                      ? "flex flex-col p-3 items-stretch"
                      : "flex flex-wrap p-3 items-center"
            }`}
            style={
                simpleLayout
                    ? {}
                    : { borderBottom: "1px solid var(--hair)" }
            }
        >
            <label className="flex items-center gap-2 cursor-pointer">
                <input
                    type="checkbox"
                    checked={showVolume}
                    onChange={(e) => setShowVolume(e.target.checked)}
                    className="rounded"
                />
                <span style={{ color: "var(--txt)", fontSize: 12.5 }}>
                    {language === "ar" ? "الحجم" : "Volume"}
                </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
                <input
                    type="checkbox"
                    checked={showOpenInterest}
                    onChange={(e) => setShowOpenInterest(e.target.checked)}
                    className="rounded"
                />
                <span style={{ color: "var(--txt)", fontSize: 12.5 }}>
                    {language === "ar" ? "الفتح المفتوح" : "Open Interest"}
                </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer" title={language === "ar" ? "دمج الحجم مع الفتح المفتوح في رسم واحد" : "Merge Volume + Open Interest into one chart"}>
                <input
                    type="checkbox"
                    checked={mergeVolumeOI}
                    onChange={(e) => setMergeVolumeOI(e.target.checked)}
                    className="rounded"
                />
                <span style={{ color: "var(--txt)", fontSize: 12.5 }}>
                    {language === "ar" ? "دمج الحجم + OI" : "Merge Vol + OI"}
                </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
                <input
                    type="checkbox"
                    checked={showSpotGamma}
                    onChange={(e) => setShowSpotGamma(e.target.checked)}
                    className="rounded"
                />
                <span style={{ color: "var(--txt)", fontSize: 12.5 }}>
                    {language === "ar" ? "سخيار بيع جاما" : "Spot Gamma"}
                </span>
            </label>
            {!simpleLayout && (
                <>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={expandChart}
                            onChange={(e) => setExpandChart(e.target.checked)}
                            className="rounded"
                        />
                        <span style={{ color: "var(--txt)", fontSize: 12.5 }}>
                            {language === "ar"
                                ? "توسيع حجم الرسم"
                                : "Expand chart size"}
                        </span>
                    </label>
                    <label
                        className="flex items-center gap-2 cursor-pointer"
                        title={
                            language === "ar"
                                ? "عرض خطوط بدل الأعمدة"
                                : "Lines instead of bars"
                        }
                    >
                        <input
                            type="checkbox"
                            checked={linearChart}
                            onChange={(e) => setLinear(e.target.checked)}
                            className="rounded"
                        />
                        <span style={{ color: "var(--txt)", fontSize: 12.5 }}>
                            {language === "ar" ? "خطي" : "Linear"}
                        </span>
                    </label>
                </>
            )}
            <label
                className={`flex items-center gap-2 ${showSpotGamma ? "cursor-pointer" : "cursor-not-allowed opacity-50"}`}
                title={
                    language === "ar"
                        ? "عرض تعرض جاما بدل مؤشر سخيار بيع جاما (كالرسم العمودي)"
                        : "Show gamma exposure (GEX) instead of spot gamma index (same as the vertical chart)"
                }
            >
                <input
                    type="checkbox"
                    checked={showGammaExposure}
                    disabled={!showSpotGamma}
                    onChange={(e) => setShowGammaExposure(e.target.checked)}
                    className="rounded"
                />
                <span style={{ color: "var(--txt)", fontSize: 12.5 }}>
                    {language === "ar" ? "تعرض جاما" : "Gamma exposure"}
                </span>
            </label>
            <label
                className="flex items-center gap-2 cursor-pointer"
                title={
                    language === "ar"
                        ? "عرض تعرض جاما على الحجم (يستخدم الحجم بدل الفتح المفتوح)"
                        : "Show gamma exposure based on volume (uses volume instead of open interest)"
                }
            >
                <input
                    type="checkbox"
                    checked={showVolumeGammaExposure}
                    onChange={(e) => setShowVolumeGammaExposure(e.target.checked)}
                    className="rounded"
                />
                <span style={{ color: "var(--txt)", fontSize: 12.5 }}>
                    {language === "ar" ? "تعرض جاما على الحجم" : "Volume gamma exposure"}
                </span>
            </label>
            {showVolumeGammaExposure && (
                <label
                    className="flex items-center gap-2 cursor-pointer"
                    title={
                        language === "ar"
                            ? "دمج تعرض جاما على الحجم مع تعرض جاما العادي (على الفتح المفتوح)"
                            : "Merge volume gamma exposure with OI-based gamma exposure"
                    }
                >
                    <input
                        type="checkbox"
                        checked={mergeVolumeGexWithOI}
                        onChange={(e) => setMergeVolumeGexWithOI(e.target.checked)}
                        className="rounded"
                    />
                    <span style={{ color: "var(--txt)", fontSize: 12.5 }}>
                        {language === "ar" ? "دمج مع الفتح المفتوح" : "Merge with OI"}
                    </span>
                </label>
            )}
            <label
                className={`flex items-center gap-2 ${useExpiryStackCharts ? "cursor-pointer" : "cursor-not-allowed opacity-50"}`}
                title={
                    language === "ar"
                        ? "في وضع تواريخ متعددة: عرض أشرطة Call وPut جنبًا إلى جنب بدل الاتجاه المعاكس."
                        : "In multi-expiry mode: show Call and Put bars side-by-side instead of mirrored."
                }
            >
                <input
                    type="checkbox"
                    checked={sideBySideCallsPuts}
                    disabled={!useExpiryStackCharts}
                    onChange={(e) => setSideBySideCallsPuts(e.target.checked)}
                    className="rounded"
                />
                <span style={{ color: "var(--txt)", fontSize: 12.5 }}>
                    {language === "ar"
                        ? "Call/Put جنبًا إلى جنب"
                        : "Side-by-side Calls/Puts"}
                </span>
            </label>
            <label
                className={`flex items-center gap-2 ${useExpiryStackCharts ? "cursor-pointer" : "cursor-not-allowed opacity-50"}`}
                title={
                    language === "ar"
                        ? "تجميع كل تواريخ كل جانب في عمود واحد لكل جانب."
                        : "Stack each side into one bar per side (calls vs puts)."
                }
            >
                <input
                    type="checkbox"
                    checked={stackEachSide}
                    disabled={!useExpiryStackCharts || stackAllSides}
                    onChange={(e) => setStackEachSide(e.target.checked)}
                    className="rounded"
                />
                <span style={{ color: "var(--txt)", fontSize: 12.5 }}>
                    {language === "ar" ? "تكديس كل جانب" : "Stack each side"}
                </span>
            </label>
            <label
                className={`flex items-center gap-2 ${useExpiryStackCharts ? "cursor-pointer" : "cursor-not-allowed opacity-50"}`}
                title={
                    language === "ar"
                        ? "تكديس كل Call وPut معًا في عمود واحد لكل سعر."
                        : "Stack calls and puts together into one bar per strike."
                }
            >
                <input
                    type="checkbox"
                    checked={stackAllSides}
                    disabled={!useExpiryStackCharts}
                    onChange={(e) => setStackAllSides(e.target.checked)}
                    className="rounded"
                />
                <span style={{ color: "var(--txt)", fontSize: 12.5 }}>
                    {language === "ar"
                        ? "تكديس الكل في عمَود واحد"
                        : "Stack all into one bar"}
                </span>
            </label>
        </section>
    );

    return (
        <>
            <style>{legendCss}</style>
            <div
                className={`${chartScopeClass} vsg-ds-root flex flex-col min-h-0 ${simpleLayout ? "" : "rounded-lg"}`}
                style={shellStyle}
            >
                {simpleLayout && simpleDrawerOpen && (
                    <button
                        type="button"
                        aria-label={language === "ar" ? "إغلاق" : "Close"}
                        onClick={() => setSimpleDrawerOpen(false)}
                        style={{
                            position: "fixed",
                            inset: 0,
                            zIndex: 120,
                            border: "none",
                            margin: 0,
                            padding: 0,
                            background: "rgba(0,0,0,0.45)",
                            cursor: "pointer",
                            WebkitTapHighlightColor: "transparent",
                            touchAction: "manipulation",
                        }}
                    />
                )}
                {simpleLayout && (
                    <aside
                        aria-hidden={!simpleDrawerOpen}
                        style={{
                            position: "fixed",
                            top: 0,
                            bottom: 0,
                            left: 0,
                            paddingTop: "env(safe-area-inset-top, 0px)",
                            width: "min(88vw, 320px)",
                            zIndex: 130,
                            backgroundColor: colors.surface,
                            color: colors.text,
                            borderRight: `1px solid ${colors.surfaceBorder}`,
                            boxShadow: simpleDrawerOpen
                                ? "8px 0 24px rgba(0,0,0,0.25)"
                                : "none",
                            transform: simpleDrawerOpen
                                ? "translateX(0)"
                                : "translateX(-102%)",
                            transition: "transform 0.22s ease",
                            display: "flex",
                            flexDirection: "column",
                            pointerEvents: simpleDrawerOpen ? "auto" : "none",
                        }}
                    >
                        <div
                            style={{
                                padding: "12px 14px",
                                borderBottom: `1px solid ${colors.surfaceBorder}`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 8,
                            }}
                        >
                            <span style={{ fontWeight: 600, fontSize: 14 }}>
                                {language === "ar"
                                    ? "خيارات العرض"
                                    : "Display options"}
                            </span>
                            <button
                                type="button"
                                onClick={() => setSimpleDrawerOpen(false)}
                                style={{
                                    ...mmecSimpleCloseBtnStyle,
                                    color: colors.textMuted,
                                }}
                                aria-label="Close"
                            >
                                ×
                            </button>
                        </div>
                        <div
                            style={{
                                overflow: "auto",
                                flex: 1,
                                padding: 12,
                                paddingLeft: `max(12px, env(safe-area-inset-left, 0px))`,
                                paddingRight: `max(12px, env(safe-area-inset-right, 0px))`,
                            }}
                        >
                            <div
                                style={{
                                    marginBottom: 12,
                                    fontSize: 12,
                                    color: colors.textMuted,
                                }}
                            >
                                {language === "ar"
                                    ? "آخر تحديث:"
                                    : "Last updated:"}{" "}
                                {new Date().toLocaleString(undefined, {
                                    dateStyle: "medium",
                                    timeStyle: "short",
                                })}
                            </div>
                            {filterBlock}
                            {togglesBlock}
                            <div
                                style={{
                                    marginTop: 16,
                                    paddingTop: 12,
                                    borderTop: `1px solid ${colors.surfaceBorder}`,
                                }}
                            >
                                <span
                                    className="block text-xs font-semibold mb-2"
                                    style={{ color: colors.textSecondary }}
                                >
                                    {language === "ar"
                                        ? "عرض الرسم"
                                        : "Chart display"}
                                </span>
                                <label className="flex items-center gap-2 cursor-pointer mb-2">
                                    <input
                                        type="checkbox"
                                        checked={linearChart}
                                        onChange={(e) =>
                                            setLinear(e.target.checked)
                                        }
                                        className="rounded"
                                    />
                                    <span
                                        className="text-sm"
                                        style={{ color: colors.text }}
                                    >
                                        {language === "ar"
                                            ? "خطي (خطوط)"
                                            : "Linear (lines)"}
                                    </span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={expandChart}
                                        onChange={(e) =>
                                            setExpandChart(e.target.checked)
                                        }
                                        className="rounded"
                                    />
                                    <span
                                        className="text-sm"
                                        style={{ color: colors.text }}
                                    >
                                        {language === "ar"
                                            ? "توسيع الارتفاع"
                                            : "Taller panels"}
                                    </span>
                                </label>
                            </div>
                        </div>
                    </aside>
                )}
                {simpleLayout && (
                    <button
                        type="button"
                        onClick={() => setSimpleDrawerOpen((o) => !o)}
                        aria-expanded={simpleDrawerOpen}
                        aria-label={
                            language === "ar" ? "فتح الخيارات" : "Open options"
                        }
                        style={mmecSimpleFabStyle({
                            left: 8,
                            top: "50%",
                            primary: colors.primary,
                            textInverse: colors.textInverse,
                            border: colors.surfaceBorder,
                        })}
                    >
                        ☰
                    </button>
                )}

                <header
                    className={`vsg-ds-header shrink-0 flex flex-wrap items-center justify-between gap-2 ${simpleLayout ? "px-3 py-2 pl-14" : "px-4 py-2"}`}
                    style={{
                        borderBottom: "1px solid var(--hair)",
                    }}
                >
                    <h2
                        className={
                            simpleLayout
                                ? "vsg-ds-title text-sm font-semibold truncate"
                                : "vsg-ds-title font-semibold"
                        }
                        style={{ color: "var(--txt)", fontSize: simpleLayout ? undefined : 18 }}
                    >
                        {symbol
                            ? `${symbol} ${language === "ar" ? "تفصيل الخيارات" : "Option Breakdown"}`
                            : language === "ar"
                              ? "الحجم / الـ OI وسخيار بيع جاما"
                              : "Volume / OI & Spot Gamma"}
                    </h2>
                    {!simpleLayout && (
                        <span
                            className="vsg-ds-updated"
                            style={{ color: "var(--muted)", fontSize: 11, fontFamily: "var(--font-mono)" }}
                        >
                            {language === "ar" ? "آخر تحديث:" : "Last updated:"}{" "}
                            {new Date().toLocaleString(undefined, {
                                dateStyle: "medium",
                                timeStyle: "short",
                            })}
                        </span>
                    )}
                </header>

                {!simpleLayout && (
                    <ProToolFilterSlot>
                        <>
                            {filterBlock}
                            {togglesBlock}
                        </>
                    </ProToolFilterSlot>
                )}

                <section
                    className={`flex-1 min-h-0 flex flex-col gap-4 ${simpleLayout ? "p-2 overflow-y-auto" : "p-3"}`}
                    style={
                        simpleLayout
                            ? { minHeight: 0 }
                            : {
                                  minHeight:
                                      (mergeVolumeOI && showVolume && showOpenInterest
                                          ? panelHeight
                                          : (showVolume ? panelHeight : 0) +
                                            (showOpenInterest ? panelHeight : 0)) +
                                      (showSpotGamma ? panelHeight : 0) +
                                      (showVolumeGammaExposure ? panelHeight : 0) +
                                      32,
                              }
                    }
                >
                    {dsOn && (loading || (loadFailed && !chartData?.length) || !chartRows.length) ? (
                        loading ? (
                            <ToolState kind="loading" lang={language === "ar" ? "ar" : "en"} fill rows={6} />
                        ) : loadFailed && !chartData?.length ? (
                            <ToolState
                                kind="error"
                                lang={language === "ar" ? "ar" : "en"}
                                fill
                                body={language === "ar" ? "فشل تحميل البيانات. تحقق من الاتصال بالخادم." : "Failed to load data. Check backend connection."}
                            />
                        ) : (
                            <ToolState
                                kind="empty"
                                lang={language === "ar" ? "ar" : "en"}
                                fill
                                title={language === "ar" ? "لا توجد بيانات للعرض" : "No data to display"}
                            />
                        )
                    ) : loading ? (
                        <p
                            className="py-8 text-center"
                            style={{ color: colors.text }}
                        >
                            {language === "ar"
                                ? "جاري تحميل البيانات (قد يستغرق بضع ثوانٍ)..."
                                : "Loading data (may take a few seconds)…"}
                        </p>
                    ) : loadFailed && !chartData?.length ? (
                        <p
                            className="py-8 text-center"
                            style={{ color: colors.text }}
                        >
                            {language === "ar"
                                ? "فشل تحميل البيانات. تحقق من الاتصال بالخادم."
                                : "Failed to load data. Check backend connection."}
                        </p>
                    ) : !chartRows.length ? (
                        <p
                            className="py-8 text-center"
                            style={{ color: colors.text }}
                        >
                            {language === "ar"
                                ? "لا توجد بيانات للعرض"
                                : "No data to display"}
                        </p>
                    ) : (
                        <>
                            {/* Panel 1: Volume Breakdown – same grid alignment via shared margin and data */}
                            {showVolume && !mergeVolumeOI && (
                                <div className="shrink-0 vsg-ds-panel">
                                    <h3 className="mb-1 vsg-ds-panel-title" style={{ fontSize: 14, fontWeight: 600, color: "var(--txt)" }}>
                                        {symbol
                                            ? `${symbol} ${language === "ar" ? "تفصيل الحجم" : "Volume Breakdown"}`
                                            : language === "ar"
                                              ? "تفصيل الحجم"
                                              : "Volume Breakdown"}
                                    </h3>
                                    {useExpiryStackCharts && (
                                        <p
                                            className="text-xs mb-1"
                                            style={{ color: colors.textMuted }}
                                        >
                                            {language === "ar"
                                                ? "ألوان حسب تاريخ الانتهاء — مجموع الحجم لكل سعر سعر التنفيذ يطابق نفس الإجمالي السابق."
                                                : "Colors = expiry; stacked volume per strike equals combined total."}
                                        </p>
                                    )}
                                    {!hasVolumeData ? (
                                        <p
                                            className="py-6 text-center text-sm opacity-80"
                                            style={{ color: colors.text }}
                                        >
                                            {noVolumeLabel}
                                        </p>
                                    ) : (
                                        <div
                                            style={{
                                                overflowX: needsScroll
                                                    ? "auto"
                                                    : "visible",
                                                width: "100%",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width:
                                                        scrollWidth ?? "100%",
                                                    minWidth: "100%",
                                                }}
                                            >
                                                <ResponsiveContainer
                                                    width="100%"
                                                    height={panelHeight}
                                                >
                                                    {linearChart ? (
                                                        useExpiryStackCharts &&
                                                        stackExpiryList ? (
                                                            <LineChart
                                                                data={
                                                                    chartRows as Record<
                                                                        string,
                                                                        unknown
                                                                    >[]
                                                                }
                                                                margin={
                                                                    CHART_MARGIN
                                                                }
                                                            >
                                                                <CartesianGrid
                                                                    strokeDasharray="3 4"
                                                                    stroke={
                                                                        gridClr
                                                                    }
                                                                    opacity={
                                                                        0.5
                                                                    }
                                                                    vertical
                                                                    horizontal
                                                                />
                                                                <ReferenceLine
                                                                    y={0}
                                                                    stroke={
                                                                        strokeClr
                                                                    }
                                                                    strokeWidth={
                                                                        1
                                                                    }
                                                                />
                                                                <XAxis
                                                                    {...linearXAxisProps}
                                                                />
                                                                <YAxis
                                                                    stroke={
                                                                        strokeClr
                                                                    }
                                                                    fontSize={
                                                                        11
                                                                    }
                                                                    tickLine={
                                                                        false
                                                                    }
                                                                    axisLine={
                                                                        false
                                                                    }
                                                                    tickFormatter={(
                                                                        v,
                                                                    ) =>
                                                                        formatAxisK(
                                                                            Number(
                                                                                v,
                                                                            ),
                                                                        )
                                                                    }
                                                                    width={42}
                                                                />
                                                                <Tooltip
                                                                    content={
                                                                        stackExpiryTooltip
                                                                    }
                                                                />
                                                                <Legend content={CustomLegend} wrapperStyle={legendWrapperStyle} />
                                                                {stackExpiryList.map(
                                                                    (
                                                                        exp,
                                                                        i,
                                                                    ) => (
                                                                        <React.Fragment
                                                                            key={
                                                                                exp
                                                                            }
                                                                        >
                                                                            <Line
                                                                                type="monotone"
                                                                                dataKey={`callVol_${expiryLabelToStackBase(exp)}`}
                                                                                name={
                                                                                    language ===
                                                                                    "ar"
                                                                                        ? `${shortenExpiryLegend(exp)} كال حجم`
                                                                                        : `${shortenExpiryLegend(exp)} Call Vol`
                                                                                }
                                                                                stroke={sideStackColor(
                                                                                    i,
                                                                                    stackExpiryList.length,
                                                                                    true,
                                                                                    isDarkMode,
                                                                                )}
                                                                                strokeWidth={
                                                                                    2
                                                                                }
                                                                                dot={
                                                                                    LINE_DOT_INVISIBLE
                                                                                }
                                                                                activeDot={{
                                                                                    ...LINE_ACTIVE_DOT,
                                                                                    fill: sideStackColor(
                                                                                        i,
                                                                                        stackExpiryList.length,
                                                                                        true,
                                                                                        isDarkMode,
                                                                                    ),
                                                                                }}
                                                                                onClick={onLinePointClick(
                                                                                    "CALL",
                                                                                )}
                                                                            />
                                                                            <Line
                                                                                type="monotone"
                                                                                dataKey={`${sideBySideCallsPuts ? "putVol" : "putVolNeg"}_${expiryLabelToStackBase(exp)}`}
                                                                                name={
                                                                                    language ===
                                                                                    "ar"
                                                                                        ? `${shortenExpiryLegend(exp)} خيار بيع حجم`
                                                                                        : `${shortenExpiryLegend(exp)} Put Vol`
                                                                                }
                                                                                stroke={sideStackColor(
                                                                                    i,
                                                                                    stackExpiryList.length,
                                                                                    false,
                                                                                    isDarkMode,
                                                                                )}
                                                                                strokeWidth={
                                                                                    2
                                                                                }
                                                                                dot={
                                                                                    LINE_DOT_INVISIBLE
                                                                                }
                                                                                activeDot={{
                                                                                    ...LINE_ACTIVE_DOT,
                                                                                    fill: sideStackColor(
                                                                                        i,
                                                                                        stackExpiryList.length,
                                                                                        false,
                                                                                        isDarkMode,
                                                                                    ),
                                                                                }}
                                                                                onClick={onLinePointClick(
                                                                                    "PUT",
                                                                                )}
                                                                            />
                                                                        </React.Fragment>
                                                                    ),
                                                                )}
                                                            </LineChart>
                                                        ) : (
                                                            <LineChart
                                                                data={
                                                                    chartRows as typeof byStrike
                                                                }
                                                                margin={
                                                                    CHART_MARGIN
                                                                }
                                                            >
                                                                <CartesianGrid
                                                                    strokeDasharray="3 4"
                                                                    stroke={
                                                                        gridClr
                                                                    }
                                                                    opacity={
                                                                        0.5
                                                                    }
                                                                    vertical
                                                                    horizontal
                                                                />
                                                                <XAxis
                                                                    {...linearXAxisProps}
                                                                />
                                                                <YAxis
                                                                    stroke={
                                                                        strokeClr
                                                                    }
                                                                    fontSize={
                                                                        11
                                                                    }
                                                                    tickLine={
                                                                        false
                                                                    }
                                                                    axisLine={
                                                                        false
                                                                    }
                                                                    tickFormatter={(
                                                                        v,
                                                                    ) =>
                                                                        formatAxisK(
                                                                            Number(
                                                                                v,
                                                                            ),
                                                                        )
                                                                    }
                                                                    width={42}
                                                                />
                                                                <Tooltip
                                                                    contentStyle={
                                                                        tooltipContentStyle
                                                                    }
                                                                    formatter={(
                                                                        v: number,
                                                                    ) => [
                                                                        Number(
                                                                            v,
                                                                        ).toLocaleString(),
                                                                        "",
                                                                    ]}
                                                                    labelFormatter={(
                                                                        l,
                                                                    ) =>
                                                                        `Strike: ${l}`
                                                                    }
                                                                />
                                                                <Legend content={CustomLegend} wrapperStyle={legendWrapperStyle} />
                                                                <Line
                                                                    type="monotone"
                                                                    dataKey="callVol"
                                                                    name={
                                                                        language ===
                                                                        "ar"
                                                                            ? "كال حجم"
                                                                            : "Call Vol"
                                                                    }
                                                                    stroke={
                                                                        LINE_COL.call
                                                                    }
                                                                    strokeWidth={
                                                                        2
                                                                    }
                                                                    dot={
                                                                        LINE_DOT_INVISIBLE
                                                                    }
                                                                    activeDot={{
                                                                        ...LINE_ACTIVE_DOT,
                                                                        fill: LINE_COL.call,
                                                                    }}
                                                                    onClick={onLinePointClick(
                                                                        "CALL",
                                                                    )}
                                                                />
                                                                <Line
                                                                    type="monotone"
                                                                    dataKey="putVol"
                                                                    name={
                                                                        language ===
                                                                        "ar"
                                                                            ? "خيار بيع حجم"
                                                                            : "Put Vol"
                                                                    }
                                                                    stroke={
                                                                        LINE_COL.put
                                                                    }
                                                                    strokeWidth={
                                                                        2
                                                                    }
                                                                    dot={
                                                                        LINE_DOT_INVISIBLE
                                                                    }
                                                                    activeDot={{
                                                                        ...LINE_ACTIVE_DOT,
                                                                        fill: LINE_COL.put,
                                                                    }}
                                                                    onClick={onLinePointClick(
                                                                        "PUT",
                                                                    )}
                                                                />
                                                            </LineChart>
                                                        )
                                                    ) : useExpiryStackCharts &&
                                                      stackExpiryList ? (
                                                        <BarChart
                                                            data={
                                                                chartRows as Record<
                                                                    string,
                                                                    unknown
                                                                >[]
                                                            }
                                                            margin={
                                                                CHART_MARGIN
                                                            }
                                                        >
                                                            <CartesianGrid
                                                                strokeDasharray="3 4"
                                                                stroke={gridClr}
                                                                opacity={0.5}
                                                                vertical={true}
                                                                horizontal={
                                                                    true
                                                                }
                                                            />
                                                            {!sideBySideCallsPuts &&
                                                                !stackAllSides && (
                                                                    <ReferenceLine
                                                                        y={0}
                                                                        stroke={
                                                                            strokeClr
                                                                        }
                                                                        strokeWidth={
                                                                            1
                                                                        }
                                                                    />
                                                                )}
                                                            <XAxis
                                                                dataKey="strike"
                                                                stroke={
                                                                    strokeClr
                                                                }
                                                                {...barTickProps}
                                                                fontSize={11}
                                                                tickLine={false}
                                                                axisLine={false}
                                                                angle={-45}
                                                                textAnchor="end"
                                                                height={48}
                                                                interval={
                                                                    xTickInterval
                                                                }
                                                            />
                                                            <YAxis
                                                                stroke={
                                                                    strokeClr
                                                                }
                                                                {...barTickProps}
                                                                fontSize={11}
                                                                tickLine={false}
                                                                axisLine={false}
                                                                tickFormatter={(
                                                                    v,
                                                                ) =>
                                                                    formatAxisK(
                                                                        Number(
                                                                            v,
                                                                        ),
                                                                    )
                                                                }
                                                                width={42}
                                                            />
                                                            <Tooltip
                                                                content={
                                                                    stackExpiryTooltip
                                                                }
                                                            />
                                                            <Legend content={CustomLegend} wrapperStyle={legendWrapperStyle} />
                                                            {stackAllSides
                                                                ? stackExpiryList.map(
                                                                      (
                                                                          exp,
                                                                          i,
                                                                      ) => (
                                                                          <Bar
                                                                              key={`all-${exp}`}
                                                                              stackId="vol_all"
                                                                              dataKey={`vol_${expiryLabelToStackBase(exp)}`}
                                                                              name={
                                                                                  language ===
                                                                                  "ar"
                                                                                      ? `${shortenExpiryLegend(exp)} إجمالي حجم`
                                                                                      : `${shortenExpiryLegend(exp)} Total Vol`
                                                                              }
                                                                              fill={
                                                                                  stackPalette[
                                                                                      i
                                                                                  ]
                                                                              }
                                                                              radius={[
                                                                                  0,
                                                                                  0,
                                                                                  0,
                                                                                  0,
                                                                              ]}
                                                                              onClick={(
                                                                                  data: unknown,
                                                                              ) =>
                                                                                  onBarClick(
                                                                                      data,
                                                                                      "CALL",
                                                                                  )
                                                                              }
                                                                              cursor="pointer"
                                                                          />
                                                                      ),
                                                                  )
                                                                : stackExpiryList.map(
                                                                      (
                                                                          exp,
                                                                          i,
                                                                      ) => (
                                                                          <React.Fragment
                                                                              key={
                                                                                  exp
                                                                              }
                                                                          >
                                                                              <Bar
                                                                                  stackId={
                                                                                      stackEachSide
                                                                                          ? "vol_call"
                                                                                          : undefined
                                                                                  }
                                                                                  dataKey={`callVol_${expiryLabelToStackBase(exp)}`}
                                                                                  name={
                                                                                      language ===
                                                                                      "ar"
                                                                                          ? `${shortenExpiryLegend(exp)} كال حجم`
                                                                                          : `${shortenExpiryLegend(exp)} Call Vol`
                                                                                  }
                                                                                  fill={sideStackColor(
                                                                                      i,
                                                                                      stackExpiryList.length,
                                                                                      true,
                                                                                      isDarkMode,
                                                                                  )}
                                                                                  radius={[
                                                                                      0,
                                                                                      0,
                                                                                      0,
                                                                                      0,
                                                                                  ]}
                                                                                  onClick={(
                                                                                      data: unknown,
                                                                                  ) =>
                                                                                      onBarClick(
                                                                                          data,
                                                                                          "CALL",
                                                                                      )
                                                                                  }
                                                                                  cursor="pointer"
                                                                              />
                                                                              <Bar
                                                                                  stackId={
                                                                                      stackEachSide
                                                                                          ? "vol_put"
                                                                                          : undefined
                                                                                  }
                                                                                  dataKey={`${sideBySideCallsPuts ? "putVol" : "putVolNeg"}_${expiryLabelToStackBase(exp)}`}
                                                                                  name={
                                                                                      language ===
                                                                                      "ar"
                                                                                          ? `${shortenExpiryLegend(exp)} خيار بيع حجم`
                                                                                          : `${shortenExpiryLegend(exp)} Put Vol`
                                                                                  }
                                                                                  fill={sideStackColor(
                                                                                      i,
                                                                                      stackExpiryList.length,
                                                                                      false,
                                                                                      isDarkMode,
                                                                                  )}
                                                                                  radius={[
                                                                                      0,
                                                                                      0,
                                                                                      0,
                                                                                      0,
                                                                                  ]}
                                                                                  onClick={(
                                                                                      data: unknown,
                                                                                  ) =>
                                                                                      onBarClick(
                                                                                          data,
                                                                                          "PUT",
                                                                                      )
                                                                                  }
                                                                                  cursor="pointer"
                                                                              />
                                                                          </React.Fragment>
                                                                      ),
                                                                  )}
                                                        </BarChart>
                                                    ) : (
                                                        <BarChart
                                                            data={
                                                                chartRows as typeof byStrike
                                                            }
                                                            margin={
                                                                CHART_MARGIN
                                                            }
                                                        >
                                                            <CartesianGrid
                                                                strokeDasharray="3 4"
                                                                stroke={gridClr}
                                                                opacity={0.5}
                                                                vertical={true}
                                                                horizontal={
                                                                    true
                                                                }
                                                            />
                                                            <XAxis
                                                                dataKey="strike"
                                                                stroke={
                                                                    strokeClr
                                                                }
                                                                {...barTickProps}
                                                                fontSize={11}
                                                                tickLine={false}
                                                                axisLine={false}
                                                                angle={-45}
                                                                textAnchor="end"
                                                                height={48}
                                                                interval={
                                                                    xTickInterval
                                                                }
                                                            />
                                                            <YAxis
                                                                stroke={
                                                                    strokeClr
                                                                }
                                                                {...barTickProps}
                                                                fontSize={11}
                                                                tickLine={false}
                                                                axisLine={false}
                                                                tickFormatter={(
                                                                    v,
                                                                ) =>
                                                                    formatAxisK(
                                                                        Number(
                                                                            v,
                                                                        ),
                                                                    )
                                                                }
                                                                width={42}
                                                            />
                                                            <Tooltip
                                                                contentStyle={
                                                                    tooltipContentStyle
                                                                }
                                                                formatter={(
                                                                    v: number,
                                                                ) => [
                                                                    Number(
                                                                        v,
                                                                    ).toLocaleString(),
                                                                    "",
                                                                ]}
                                                                labelFormatter={(
                                                                    l,
                                                                ) =>
                                                                    `Strike: ${l}`
                                                                }
                                                            />
                                                            <Legend content={CustomLegend} wrapperStyle={legendWrapperStyle} />
                                                            <Bar
                                                                dataKey="putVol"
                                                                name={
                                                                    language ===
                                                                    "ar"
                                                                        ? "خيار بيع حجم"
                                                                        : "Put Vol"
                                                                }
                                                                fill={chart.down}
                                                                radius={[
                                                                    0, 0, 0, 0,
                                                                ]}
                                                                onClick={(
                                                                    data: any,
                                                                ) =>
                                                                    onBarClick(
                                                                        data,
                                                                        "PUT",
                                                                    )
                                                                }
                                                                cursor="pointer"
                                                            />
                                                            <Bar
                                                                dataKey="callVol"
                                                                name={
                                                                    language ===
                                                                    "ar"
                                                                        ? "كال حجم"
                                                                        : "Call Vol"
                                                                }
                                                                fill={chart.up}
                                                                radius={[
                                                                    0, 0, 0, 0,
                                                                ]}
                                                                onClick={(
                                                                    data: any,
                                                                ) =>
                                                                    onBarClick(
                                                                        data,
                                                                        "CALL",
                                                                    )
                                                                }
                                                                cursor="pointer"
                                                            />
                                                        </BarChart>
                                                    )}
                                                </ResponsiveContainer>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Merged Volume + OI panel */}
                            {mergeVolumeOI && showVolume && showOpenInterest && (
                                <div className="shrink-0 vsg-ds-panel">
                                    <h3 className="mb-1 vsg-ds-panel-title" style={{ fontSize: 14, fontWeight: 600, color: "var(--txt)" }}>
                                        {symbol
                                            ? `${symbol} ${language === "ar" ? "الحجم + الفتح المفتوح" : "Volume + Open Interest"}`
                                            : language === "ar"
                                              ? "الحجم + الفتح المفتوح"
                                              : "Volume + Open Interest"}
                                    </h3>
                                    {!hasVolumeData && !hasOIData ? (
                                        <p
                                            className="py-6 text-center text-sm opacity-80"
                                            style={{ color: colors.text }}
                                        >
                                            {language === "ar"
                                                ? "لا توجد بيانات للحجم أو الفتح المفتوح"
                                                : "No volume or open interest data"}
                                        </p>
                                    ) : (
                                        <div
                                            style={{
                                                overflowX: needsScroll
                                                    ? "auto"
                                                    : "visible",
                                                width: "100%",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width:
                                                        scrollWidth ?? "100%",
                                                    minWidth: "100%",
                                                }}
                                            >
                                                <ResponsiveContainer
                                                    width="100%"
                                                    height={panelHeight}
                                                >
                                                    <BarChart
                                                        data={
                                                            chartRows as typeof byStrike
                                                        }
                                                        margin={
                                                            CHART_MARGIN
                                                        }
                                                    >
                                                        <CartesianGrid
                                                            strokeDasharray="3 4"
                                                            stroke={gridClr}
                                                            opacity={0.5}
                                                            vertical={true}
                                                            horizontal={true}
                                                        />
                                                        <XAxis
                                                            dataKey="strike"
                                                            stroke={strokeClr}
                                                            fontSize={11}
                                                            tickLine={false}
                                                            axisLine={false}
                                                            tickFormatter={(v) => {
                                                                const n = Number(v);
                                                                if (!n) return "";
                                                                if (n >= 1000) return (n / 1000).toFixed(n % 1000 === 0 ? 0 : 1) + "k";
                                                                return n.toString();
                                                            }}
                                                        />
                                                        <YAxis
                                                            stroke={strokeClr}
                                                            fontSize={11}
                                                            tickLine={false}
                                                            axisLine={false}
                                                            tickFormatter={(v) => formatAxisK(Number(v))}
                                                            width={42}
                                                        />
                                                        <Tooltip
                                                            contentStyle={tooltipContentStyle}
                                                            formatter={(v: number) => [
                                                                Number(v).toLocaleString(),
                                                                "",
                                                            ]}
                                                            labelFormatter={(l) => `Strike: ${l}`}
                                                        />
                                                        <Legend content={CustomLegend} wrapperStyle={legendWrapperStyle} />
                                                        <Bar
                                                            dataKey="callVol"
                                                            name={language === "ar" ? "كال حجم" : "Call Vol"}
                                                            fill={chart.up}
                                                            radius={[0, 0, 0, 0]}
                                                            onClick={(
                                                                data: any,
                                                            ) =>
                                                                onBarClick(
                                                                    data,
                                                                    "CALL",
                                                                )
                                                            }
                                                            cursor="pointer"
                                                        />
                                                        <Bar
                                                            dataKey="putVol"
                                                            name={language === "ar" ? "خيار بيع حجم" : "Put Vol"}
                                                            fill={chart.down}
                                                            radius={[0, 0, 0, 0]}
                                                            onClick={(
                                                                data: any,
                                                            ) =>
                                                                onBarClick(
                                                                    data,
                                                                    "PUT",
                                                                )
                                                            }
                                                            cursor="pointer"
                                                        />
                                                        <Bar
                                                            dataKey="callOI"
                                                            name={language === "ar" ? "كال OI" : "Call OI"}
                                                            fill={chart.up}
                                                            fillOpacity={0.4}
                                                            radius={[0, 0, 0, 0]}
                                                            onClick={(
                                                                data: any,
                                                            ) =>
                                                                onBarClick(
                                                                    data,
                                                                    "CALL",
                                                                )
                                                            }
                                                            cursor="pointer"
                                                        />
                                                        <Bar
                                                            dataKey="putOI"
                                                            name={language === "ar" ? "خيار بيع OI" : "Put OI"}
                                                            fill={chart.down}
                                                            fillOpacity={0.4}
                                                            radius={[0, 0, 0, 0]}
                                                            onClick={(
                                                                data: any,
                                                            ) =>
                                                                onBarClick(
                                                                    data,
                                                                    "PUT",
                                                                )
                                                            }
                                                            cursor="pointer"
                                                        />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Panel 2: Open Interest – same grid alignment */}
                            {showOpenInterest && !mergeVolumeOI && (
                                <div className="shrink-0 vsg-ds-panel">
                                    <h3 className="mb-1 vsg-ds-panel-title" style={{ fontSize: 14, fontWeight: 600, color: "var(--txt)" }}>
                                        {symbol
                                            ? `${symbol} ${language === "ar" ? "الفتح المفتوح" : "Open Interest"}`
                                            : language === "ar"
                                              ? "الفتح المفتوح"
                                              : "Open Interest"}
                                    </h3>
                                    {!hasOIData ? (
                                        <p
                                            className="py-6 text-center text-sm opacity-80"
                                            style={{ color: colors.text }}
                                        >
                                            {missingDataLabel}
                                        </p>
                                    ) : (
                                        <div
                                            style={{
                                                overflowX: needsScroll
                                                    ? "auto"
                                                    : "visible",
                                                width: "100%",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width:
                                                        scrollWidth ?? "100%",
                                                    minWidth: "100%",
                                                }}
                                            >
                                                <ResponsiveContainer
                                                    width="100%"
                                                    height={panelHeight}
                                                >
                                                    {linearChart ? (
                                                        useExpiryStackCharts &&
                                                        stackExpiryList ? (
                                                            <LineChart
                                                                data={
                                                                    chartRows as Record<
                                                                        string,
                                                                        unknown
                                                                    >[]
                                                                }
                                                                margin={
                                                                    CHART_MARGIN
                                                                }
                                                            >
                                                                <CartesianGrid
                                                                    strokeDasharray="3 4"
                                                                    stroke={
                                                                        gridClr
                                                                    }
                                                                    opacity={
                                                                        0.5
                                                                    }
                                                                    vertical
                                                                    horizontal
                                                                />
                                                                <ReferenceLine
                                                                    y={0}
                                                                    stroke={
                                                                        strokeClr
                                                                    }
                                                                    strokeWidth={
                                                                        1
                                                                    }
                                                                />
                                                                <XAxis
                                                                    {...linearXAxisProps}
                                                                />
                                                                <YAxis
                                                                    stroke={
                                                                        strokeClr
                                                                    }
                                                                    fontSize={
                                                                        11
                                                                    }
                                                                    tickLine={
                                                                        false
                                                                    }
                                                                    axisLine={
                                                                        false
                                                                    }
                                                                    tickFormatter={(
                                                                        v,
                                                                    ) =>
                                                                        formatAxisK(
                                                                            Number(
                                                                                v,
                                                                            ),
                                                                        )
                                                                    }
                                                                    width={42}
                                                                />
                                                                <Tooltip
                                                                    content={
                                                                        stackExpiryTooltip
                                                                    }
                                                                />
                                                                <Legend content={CustomLegend} wrapperStyle={legendWrapperStyle} />
                                                                {stackExpiryList.map(
                                                                    (
                                                                        exp,
                                                                        i,
                                                                    ) => (
                                                                        <React.Fragment
                                                                            key={
                                                                                exp
                                                                            }
                                                                        >
                                                                            <Line
                                                                                type="monotone"
                                                                                dataKey={`callOI_${expiryLabelToStackBase(exp)}`}
                                                                                name={
                                                                                    language ===
                                                                                    "ar"
                                                                                        ? `${shortenExpiryLegend(exp)} كال OI`
                                                                                        : `${shortenExpiryLegend(exp)} Call OI`
                                                                                }
                                                                                stroke={sideStackColor(
                                                                                    i,
                                                                                    stackExpiryList.length,
                                                                                    true,
                                                                                    isDarkMode,
                                                                                )}
                                                                                strokeWidth={
                                                                                    2
                                                                                }
                                                                                dot={
                                                                                    LINE_DOT_INVISIBLE
                                                                                }
                                                                                activeDot={{
                                                                                    ...LINE_ACTIVE_DOT,
                                                                                    fill: sideStackColor(
                                                                                        i,
                                                                                        stackExpiryList.length,
                                                                                        true,
                                                                                        isDarkMode,
                                                                                    ),
                                                                                }}
                                                                                onClick={onLinePointClick(
                                                                                    "CALL",
                                                                                )}
                                                                            />
                                                                            <Line
                                                                                type="monotone"
                                                                                dataKey={`${sideBySideCallsPuts ? "putOI" : "putOINeg"}_${expiryLabelToStackBase(exp)}`}
                                                                                name={
                                                                                    language ===
                                                                                    "ar"
                                                                                        ? `${shortenExpiryLegend(exp)} خيار بيع OI`
                                                                                        : `${shortenExpiryLegend(exp)} Put OI`
                                                                                }
                                                                                stroke={sideStackColor(
                                                                                    i,
                                                                                    stackExpiryList.length,
                                                                                    false,
                                                                                    isDarkMode,
                                                                                )}
                                                                                strokeWidth={
                                                                                    2
                                                                                }
                                                                                dot={
                                                                                    LINE_DOT_INVISIBLE
                                                                                }
                                                                                activeDot={{
                                                                                    ...LINE_ACTIVE_DOT,
                                                                                    fill: sideStackColor(
                                                                                        i,
                                                                                        stackExpiryList.length,
                                                                                        false,
                                                                                        isDarkMode,
                                                                                    ),
                                                                                }}
                                                                                onClick={onLinePointClick(
                                                                                    "PUT",
                                                                                )}
                                                                            />
                                                                        </React.Fragment>
                                                                    ),
                                                                )}
                                                            </LineChart>
                                                        ) : (
                                                            <LineChart
                                                                data={
                                                                    chartRows as typeof byStrike
                                                                }
                                                                margin={
                                                                    CHART_MARGIN
                                                                }
                                                            >
                                                                <CartesianGrid
                                                                    strokeDasharray="3 4"
                                                                    stroke={
                                                                        gridClr
                                                                    }
                                                                    opacity={
                                                                        0.5
                                                                    }
                                                                    vertical
                                                                    horizontal
                                                                />
                                                                <XAxis
                                                                    {...linearXAxisProps}
                                                                />
                                                                <YAxis
                                                                    stroke={
                                                                        strokeClr
                                                                    }
                                                                    fontSize={
                                                                        11
                                                                    }
                                                                    tickLine={
                                                                        false
                                                                    }
                                                                    axisLine={
                                                                        false
                                                                    }
                                                                    tickFormatter={(
                                                                        v,
                                                                    ) =>
                                                                        formatAxisK(
                                                                            Number(
                                                                                v,
                                                                            ),
                                                                        )
                                                                    }
                                                                    width={42}
                                                                />
                                                                <Tooltip
                                                                    contentStyle={
                                                                        tooltipContentStyle
                                                                    }
                                                                    formatter={(
                                                                        v: number,
                                                                    ) => [
                                                                        Number(
                                                                            v,
                                                                        ).toLocaleString(),
                                                                        "",
                                                                    ]}
                                                                    labelFormatter={(
                                                                        l,
                                                                    ) =>
                                                                        `Strike: ${l}`
                                                                    }
                                                                />
                                                                <Legend content={CustomLegend} wrapperStyle={legendWrapperStyle} />
                                                                <Line
                                                                    type="monotone"
                                                                    dataKey="callOI"
                                                                    name={
                                                                        language ===
                                                                        "ar"
                                                                            ? "كال OI"
                                                                            : "Call OI"
                                                                    }
                                                                    stroke={
                                                                        LINE_COL.call
                                                                    }
                                                                    strokeWidth={
                                                                        2
                                                                    }
                                                                    dot={
                                                                        LINE_DOT_INVISIBLE
                                                                    }
                                                                    activeDot={{
                                                                        ...LINE_ACTIVE_DOT,
                                                                        fill: LINE_COL.call,
                                                                    }}
                                                                    onClick={onLinePointClick(
                                                                        "CALL",
                                                                    )}
                                                                />
                                                                <Line
                                                                    type="monotone"
                                                                    dataKey="putOI"
                                                                    name={
                                                                        language ===
                                                                        "ar"
                                                                            ? "خيار بيع OI"
                                                                            : "Put OI"
                                                                    }
                                                                    stroke={
                                                                        LINE_COL.put
                                                                    }
                                                                    strokeWidth={
                                                                        2
                                                                    }
                                                                    dot={
                                                                        LINE_DOT_INVISIBLE
                                                                    }
                                                                    activeDot={{
                                                                        ...LINE_ACTIVE_DOT,
                                                                        fill: LINE_COL.put,
                                                                    }}
                                                                    onClick={onLinePointClick(
                                                                        "PUT",
                                                                    )}
                                                                />
                                                            </LineChart>
                                                        )
                                                    ) : useExpiryStackCharts &&
                                                      stackExpiryList ? (
                                                        <BarChart
                                                            data={
                                                                chartRows as Record<
                                                                    string,
                                                                    unknown
                                                                >[]
                                                            }
                                                            margin={
                                                                CHART_MARGIN
                                                            }
                                                        >
                                                            <CartesianGrid
                                                                strokeDasharray="3 4"
                                                                stroke={gridClr}
                                                                opacity={0.5}
                                                                vertical={true}
                                                                horizontal={
                                                                    true
                                                                }
                                                            />
                                                            {!sideBySideCallsPuts &&
                                                                !stackAllSides && (
                                                                    <ReferenceLine
                                                                        y={0}
                                                                        stroke={
                                                                            strokeClr
                                                                        }
                                                                        strokeWidth={
                                                                            1
                                                                        }
                                                                    />
                                                                )}
                                                            <XAxis
                                                                dataKey="strike"
                                                                stroke={
                                                                    strokeClr
                                                                }
                                                                {...barTickProps}
                                                                fontSize={11}
                                                                tickLine={false}
                                                                axisLine={false}
                                                                angle={-45}
                                                                textAnchor="end"
                                                                height={48}
                                                                interval={
                                                                    xTickInterval
                                                                }
                                                            />
                                                            <YAxis
                                                                stroke={
                                                                    strokeClr
                                                                }
                                                                {...barTickProps}
                                                                fontSize={11}
                                                                tickLine={false}
                                                                axisLine={false}
                                                                tickFormatter={(
                                                                    v,
                                                                ) =>
                                                                    formatAxisK(
                                                                        Number(
                                                                            v,
                                                                        ),
                                                                    )
                                                                }
                                                                width={42}
                                                            />
                                                            <Tooltip
                                                                content={
                                                                    stackExpiryTooltip
                                                                }
                                                            />
                                                            <Legend content={CustomLegend} wrapperStyle={legendWrapperStyle} />
                                                            {stackAllSides
                                                                ? stackExpiryList.map(
                                                                      (
                                                                          exp,
                                                                          i,
                                                                      ) => (
                                                                          <Bar
                                                                              key={`all-oi-${exp}`}
                                                                              stackId="oi_all"
                                                                              dataKey={`oi_${expiryLabelToStackBase(exp)}`}
                                                                              name={
                                                                                  language ===
                                                                                  "ar"
                                                                                      ? `${shortenExpiryLegend(exp)} إجمالي OI`
                                                                                      : `${shortenExpiryLegend(exp)} Total OI`
                                                                              }
                                                                              fill={
                                                                                  stackPalette[
                                                                                      i
                                                                                  ]
                                                                              }
                                                                              radius={[
                                                                                  0,
                                                                                  0,
                                                                                  0,
                                                                                  0,
                                                                              ]}
                                                                              onClick={(
                                                                                  data: unknown,
                                                                              ) =>
                                                                                  onBarClick(
                                                                                      data,
                                                                                      "CALL",
                                                                                  )
                                                                              }
                                                                              cursor="pointer"
                                                                          />
                                                                      ),
                                                                  )
                                                                : stackExpiryList.map(
                                                                      (
                                                                          exp,
                                                                          i,
                                                                      ) => (
                                                                          <React.Fragment
                                                                              key={
                                                                                  exp
                                                                              }
                                                                          >
                                                                              <Bar
                                                                                  stackId={
                                                                                      stackEachSide
                                                                                          ? "oi_call"
                                                                                          : undefined
                                                                                  }
                                                                                  dataKey={`callOI_${expiryLabelToStackBase(exp)}`}
                                                                                  name={
                                                                                      language ===
                                                                                      "ar"
                                                                                          ? `${shortenExpiryLegend(exp)} كال OI`
                                                                                          : `${shortenExpiryLegend(exp)} Call OI`
                                                                                  }
                                                                                  fill={sideStackColor(
                                                                                      i,
                                                                                      stackExpiryList.length,
                                                                                      true,
                                                                                      isDarkMode,
                                                                                  )}
                                                                                  radius={[
                                                                                      0,
                                                                                      0,
                                                                                      0,
                                                                                      0,
                                                                                  ]}
                                                                                  onClick={(
                                                                                      data: unknown,
                                                                                  ) =>
                                                                                      onBarClick(
                                                                                          data,
                                                                                          "CALL",
                                                                                      )
                                                                                  }
                                                                                  cursor="pointer"
                                                                              />
                                                                              <Bar
                                                                                  stackId={
                                                                                      stackEachSide
                                                                                          ? "oi_put"
                                                                                          : undefined
                                                                                  }
                                                                                  dataKey={`${sideBySideCallsPuts ? "putOI" : "putOINeg"}_${expiryLabelToStackBase(exp)}`}
                                                                                  name={
                                                                                      language ===
                                                                                      "ar"
                                                                                          ? `${shortenExpiryLegend(exp)} خيار بيع OI`
                                                                                          : `${shortenExpiryLegend(exp)} Put OI`
                                                                                  }
                                                                                  fill={sideStackColor(
                                                                                      i,
                                                                                      stackExpiryList.length,
                                                                                      false,
                                                                                      isDarkMode,
                                                                                  )}
                                                                                  radius={[
                                                                                      0,
                                                                                      0,
                                                                                      0,
                                                                                      0,
                                                                                  ]}
                                                                                  onClick={(
                                                                                      data: unknown,
                                                                                  ) =>
                                                                                      onBarClick(
                                                                                          data,
                                                                                          "PUT",
                                                                                      )
                                                                                  }
                                                                                  cursor="pointer"
                                                                              />
                                                                          </React.Fragment>
                                                                      ),
                                                                  )}
                                                        </BarChart>
                                                    ) : (
                                                        <BarChart
                                                            data={
                                                                chartRows as typeof byStrike
                                                            }
                                                            margin={
                                                                CHART_MARGIN
                                                            }
                                                        >
                                                            <CartesianGrid
                                                                strokeDasharray="3 4"
                                                                stroke={gridClr}
                                                                opacity={0.5}
                                                                vertical={true}
                                                                horizontal={
                                                                    true
                                                                }
                                                            />
                                                            <XAxis
                                                                dataKey="strike"
                                                                stroke={
                                                                    strokeClr
                                                                }
                                                                {...barTickProps}
                                                                fontSize={11}
                                                                tickLine={false}
                                                                axisLine={false}
                                                                angle={-45}
                                                                textAnchor="end"
                                                                height={48}
                                                                interval={
                                                                    xTickInterval
                                                                }
                                                            />
                                                            <YAxis
                                                                stroke={
                                                                    strokeClr
                                                                }
                                                                {...barTickProps}
                                                                fontSize={11}
                                                                tickLine={false}
                                                                axisLine={false}
                                                                tickFormatter={(
                                                                    v,
                                                                ) =>
                                                                    formatAxisK(
                                                                        Number(
                                                                            v,
                                                                        ),
                                                                    )
                                                                }
                                                                width={42}
                                                            />
                                                            <Tooltip
                                                                contentStyle={
                                                                    tooltipContentStyle
                                                                }
                                                                formatter={(
                                                                    v: number,
                                                                ) => [
                                                                    Number(
                                                                        v,
                                                                    ).toLocaleString(),
                                                                    "",
                                                                ]}
                                                                labelFormatter={(
                                                                    l,
                                                                ) =>
                                                                    `Strike: ${l}`
                                                                }
                                                            />
                                                            <Legend content={CustomLegend} wrapperStyle={legendWrapperStyle} />
                                                            <Bar
                                                                dataKey="putOI"
                                                                name={
                                                                    language ===
                                                                    "ar"
                                                                        ? "خيار بيع OI"
                                                                        : "Put OI"
                                                                }
                                                                fill={chart.down}
                                                                radius={[
                                                                    0, 0, 0, 0,
                                                                ]}
                                                                onClick={(
                                                                    data: any,
                                                                ) =>
                                                                    onBarClick(
                                                                        data,
                                                                        "PUT",
                                                                    )
                                                                }
                                                                cursor="pointer"
                                                            />
                                                            <Bar
                                                                dataKey="callOI"
                                                                name={
                                                                    language ===
                                                                    "ar"
                                                                        ? "كال OI"
                                                                        : "Call OI"
                                                                }
                                                                fill={chart.up}
                                                                radius={[
                                                                    0, 0, 0, 0,
                                                                ]}
                                                                onClick={(
                                                                    data: any,
                                                                ) =>
                                                                    onBarClick(
                                                                        data,
                                                                        "CALL",
                                                                    )
                                                                }
                                                                cursor="pointer"
                                                            />
                                                        </BarChart>
                                                    )}
                                                </ResponsiveContainer>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Panel 3: Spot gamma index (Vertical chart middle) or gamma exposure (Vertical chart left); bars or line */}
                            {showSpotGamma && (
                                <div className="shrink-0 vsg-ds-panel">
                                    <h3
                                        className="mb-1"
                                        style={{ fontSize: 14, fontWeight: 600, color: "var(--txt)" }}
                                    >
                                        {symbol
                                            ? `${symbol} ${thirdPanelTitle}`
                                            : thirdPanelTitle}
                                    </h3>
                                    {!thirdPanelHasData ? (
                                        <p
                                            className="py-6 text-center text-sm opacity-80"
                                            style={{ color: colors.text }}
                                        >
                                            {missingDataLabel}
                                        </p>
                                    ) : (
                                        <div
                                            style={{
                                                overflowX: needsScroll
                                                    ? "auto"
                                                    : "visible",
                                                width: "100%",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width:
                                                        scrollWidth ?? "100%",
                                                    minWidth: "100%",
                                                }}
                                            >
                                                <ResponsiveContainer
                                                    width="100%"
                                                    height={panelHeight}
                                                >
                                                    {linearChart ? (
                                                        useExpiryStackCharts &&
                                                        stackExpiryList ? (
                                                            <LineChart
                                                                data={
                                                                    chartRows as Record<
                                                                        string,
                                                                        unknown
                                                                    >[]
                                                                }
                                                                margin={
                                                                    CHART_MARGIN
                                                                }
                                                            >
                                                                <CartesianGrid
                                                                    strokeDasharray="3 4"
                                                                    stroke={
                                                                        gridClr
                                                                    }
                                                                    opacity={
                                                                        0.5
                                                                    }
                                                                    vertical
                                                                    horizontal
                                                                />
                                                                <ReferenceLine
                                                                    y={0}
                                                                    stroke={
                                                                        strokeClr
                                                                    }
                                                                    strokeWidth={
                                                                        1
                                                                    }
                                                                />
                                                                {Number.isFinite(
                                                                    underlyingPriceFallback,
                                                                ) &&
                                                                    underlyingPriceFallback >
                                                                        0 && (
                                                                        <ReferenceLine
                                                                            x={
                                                                                underlyingPriceFallback
                                                                            }
                                                                            stroke={
                                                                                spotLineClr
                                                                            }
                                                                            strokeDasharray="4 4"
                                                                            strokeOpacity={
                                                                                0.85
                                                                            }
                                                                        />
                                                                    )}
                                                                <XAxis
                                                                    {...linearXAxisProps}
                                                                />
                                                                <YAxis
                                                                    stroke={
                                                                        strokeClr
                                                                    }
                                                                    fontSize={
                                                                        11
                                                                    }
                                                                    tickLine={
                                                                        false
                                                                    }
                                                                    axisLine={
                                                                        false
                                                                    }
                                                                    tickFormatter={(
                                                                        v,
                                                                    ) =>
                                                                        formatThirdPanelY(
                                                                            Number(
                                                                                v,
                                                                            ),
                                                                        )
                                                                    }
                                                                    width={42}
                                                                    domain={
                                                                        chartRows.length
                                                                            ? ([
                                                                                  "dataMin",
                                                                                  "dataMax",
                                                                              ] as [
                                                                                  string,
                                                                                  string,
                                                                              ])
                                                                            : undefined
                                                                    }
                                                                    allowDataOverflow
                                                                />
                                                                <Tooltip
                                                                    content={
                                                                        stackExpiryTooltip
                                                                    }
                                                                />
                                                                <Legend content={CustomLegend} wrapperStyle={legendWrapperStyle} />
                                                                {stackExpiryList.map(
                                                                    (
                                                                        exp,
                                                                        i,
                                                                    ) => (
                                                                        <Line
                                                                            key={
                                                                                exp
                                                                            }
                                                                            type="monotone"
                                                                            dataKey={
                                                                                showGammaExposure
                                                                                    ? `gex_${expiryLabelToStackBase(exp)}`
                                                                                    : `sgDisp_${expiryLabelToStackBase(exp)}`
                                                                            }
                                                                            name={
                                                                                language ===
                                                                                "ar"
                                                                                    ? `${thirdPanelSeriesLabel} ${shortenExpiryLegend(exp)}`
                                                                                    : `${shortenExpiryLegend(exp)} (${thirdPanelSeriesLabel})`
                                                                            }
                                                                            stroke={spotGammaStackFill(
                                                                                i,
                                                                            )}
                                                                            strokeWidth={
                                                                                2.5
                                                                            }
                                                                            dot={
                                                                                LINE_DOT_INVISIBLE
                                                                            }
                                                                            activeDot={{
                                                                                ...LINE_ACTIVE_DOT,
                                                                                fill: spotGammaStackFill(
                                                                                    i,
                                                                                ),
                                                                            }}
                                                                            onClick={onLinePointClick(
                                                                                "CALL",
                                                                            )}
                                                                        />
                                                                    ),
                                                                )}
                                                            </LineChart>
                                                        ) : (
                                                            <LineChart
                                                                data={
                                                                    chartRows as typeof byStrike
                                                                }
                                                                margin={
                                                                    CHART_MARGIN
                                                                }
                                                            >
                                                                <CartesianGrid
                                                                    strokeDasharray="3 4"
                                                                    stroke={
                                                                        gridClr
                                                                    }
                                                                    opacity={
                                                                        0.5
                                                                    }
                                                                    vertical
                                                                    horizontal
                                                                />
                                                                <ReferenceLine
                                                                    y={0}
                                                                    stroke={
                                                                        strokeClr
                                                                    }
                                                                    strokeWidth={
                                                                        1
                                                                    }
                                                                />
                                                                {Number.isFinite(
                                                                    underlyingPriceFallback,
                                                                ) &&
                                                                    underlyingPriceFallback >
                                                                        0 && (
                                                                        <ReferenceLine
                                                                            x={
                                                                                underlyingPriceFallback
                                                                            }
                                                                            stroke={
                                                                                spotLineClr
                                                                            }
                                                                            strokeDasharray="4 4"
                                                                            strokeOpacity={
                                                                                0.85
                                                                            }
                                                                        />
                                                                    )}
                                                                <XAxis
                                                                    {...linearXAxisProps}
                                                                />
                                                                <YAxis
                                                                    stroke={
                                                                        strokeClr
                                                                    }
                                                                    fontSize={
                                                                        11
                                                                    }
                                                                    tickLine={
                                                                        false
                                                                    }
                                                                    axisLine={
                                                                        false
                                                                    }
                                                                    tickFormatter={(
                                                                        v,
                                                                    ) =>
                                                                        formatThirdPanelY(
                                                                            Number(
                                                                                v,
                                                                            ),
                                                                        )
                                                                    }
                                                                    width={42}
                                                                    domain={
                                                                        chartRows.length
                                                                            ? ([
                                                                                  "dataMin",
                                                                                  "dataMax",
                                                                              ] as [
                                                                                  string,
                                                                                  string,
                                                                              ])
                                                                            : undefined
                                                                    }
                                                                    allowDataOverflow
                                                                />
                                                                <Tooltip
                                                                    contentStyle={
                                                                        tooltipContentStyle
                                                                    }
                                                                    formatter={(
                                                                        v: number,
                                                                    ) => [
                                                                        Number(
                                                                            v,
                                                                        ).toLocaleString(
                                                                            undefined,
                                                                            {
                                                                                maximumFractionDigits: 2,
                                                                            },
                                                                        ),
                                                                        thirdPanelSeriesLabel,
                                                                    ]}
                                                                    labelFormatter={(
                                                                        l,
                                                                    ) =>
                                                                        `Strike: ${l}`
                                                                    }
                                                                />
                                                                <Legend content={CustomLegend} wrapperStyle={legendWrapperStyle} />
                                                                <Line
                                                                    type="monotone"
                                                                    dataKey={
                                                                        thirdPanelDataKey
                                                                    }
                                                                    name={
                                                                        thirdPanelSeriesLabel
                                                                    }
                                                                    stroke={
                                                                        thirdPanelStroke
                                                                    }
                                                                    strokeWidth={
                                                                        2.5
                                                                    }
                                                                    dot={
                                                                        LINE_DOT_INVISIBLE
                                                                    }
                                                                    activeDot={{
                                                                        ...LINE_ACTIVE_DOT,
                                                                        fill: thirdPanelStroke,
                                                                    }}
                                                                    onClick={onLinePointClick(
                                                                        "CALL",
                                                                    )}
                                                                />
                                                            </LineChart>
                                                        )
                                                    ) : useExpiryStackCharts &&
                                                      stackExpiryList ? (
                                                        <BarChart
                                                            data={
                                                                chartRows as Record<
                                                                    string,
                                                                    unknown
                                                                >[]
                                                            }
                                                            margin={
                                                                CHART_MARGIN
                                                            }
                                                            barCategoryGap="10%"
                                                        >
                                                            <CartesianGrid
                                                                strokeDasharray="3 4"
                                                                stroke={gridClr}
                                                                opacity={0.5}
                                                                vertical={true}
                                                                horizontal={
                                                                    true
                                                                }
                                                            />
                                                            <ReferenceLine
                                                                y={0}
                                                                stroke={
                                                                    strokeClr
                                                                }
                                                                strokeWidth={1}
                                                            />
                                                            <XAxis
                                                                dataKey="strike"
                                                                stroke={
                                                                    strokeClr
                                                                }
                                                                {...barTickProps}
                                                                fontSize={11}
                                                                tickLine={false}
                                                                axisLine={false}
                                                                angle={-45}
                                                                textAnchor="end"
                                                                height={48}
                                                                interval={
                                                                    xTickInterval
                                                                }
                                                            />
                                                            <YAxis
                                                                stroke={
                                                                    strokeClr
                                                                }
                                                                {...barTickProps}
                                                                fontSize={11}
                                                                tickLine={false}
                                                                axisLine={false}
                                                                tickFormatter={(
                                                                    v,
                                                                ) =>
                                                                    formatThirdPanelY(
                                                                        Number(
                                                                            v,
                                                                        ),
                                                                    )
                                                                }
                                                                width={42}
                                                                domain={
                                                                    chartRows.length
                                                                        ? ([
                                                                              "dataMin",
                                                                              "dataMax",
                                                                          ] as [
                                                                              string,
                                                                              string,
                                                                          ])
                                                                        : undefined
                                                                }
                                                                allowDataOverflow
                                                            />
                                                            <Tooltip
                                                                content={
                                                                    stackExpiryTooltip
                                                                }
                                                            />
                                                            <Legend content={CustomLegend} wrapperStyle={legendWrapperStyle} />
                                                            {stackExpiryList.map(
                                                                (exp, i) => (
                                                                    <Bar
                                                                        key={
                                                                            exp
                                                                        }
                                                                        stackId={
                                                                            showGammaExposure
                                                                                ? "tpgex"
                                                                                : "tpsg"
                                                                        }
                                                                        dataKey={
                                                                            showGammaExposure
                                                                                ? `gex_${expiryLabelToStackBase(exp)}`
                                                                                : `sgDisp_${expiryLabelToStackBase(exp)}`
                                                                        }
                                                                        name={
                                                                            language ===
                                                                            "ar"
                                                                                ? `${shortenExpiryLegend(exp)}`
                                                                                : `${shortenExpiryLegend(exp)}`
                                                                        }
                                                                        fill={spotGammaStackFill(
                                                                            i,
                                                                        )}
                                                                        radius={[
                                                                            0,
                                                                            0,
                                                                            0,
                                                                            0,
                                                                        ]}
                                                                        onClick={(
                                                                            data: unknown,
                                                                        ) =>
                                                                            onBarClick(
                                                                                data,
                                                                                "CALL",
                                                                            )
                                                                        }
                                                                        cursor="pointer"
                                                                    />
                                                                ),
                                                            )}
                                                        </BarChart>
                                                    ) : (
                                                        <BarChart
                                                            data={
                                                                chartRows as typeof byStrike
                                                            }
                                                            margin={
                                                                CHART_MARGIN
                                                            }
                                                            barCategoryGap="10%"
                                                        >
                                                            <CartesianGrid
                                                                strokeDasharray="3 4"
                                                                stroke={gridClr}
                                                                opacity={0.5}
                                                                vertical={true}
                                                                horizontal={
                                                                    true
                                                                }
                                                            />
                                                            <ReferenceLine
                                                                y={0}
                                                                stroke={
                                                                    strokeClr
                                                                }
                                                                strokeWidth={1}
                                                            />
                                                            <XAxis
                                                                dataKey="strike"
                                                                stroke={
                                                                    strokeClr
                                                                }
                                                                {...barTickProps}
                                                                fontSize={11}
                                                                tickLine={false}
                                                                axisLine={false}
                                                                angle={-45}
                                                                textAnchor="end"
                                                                height={48}
                                                                interval={
                                                                    xTickInterval
                                                                }
                                                            />
                                                            <YAxis
                                                                stroke={
                                                                    strokeClr
                                                                }
                                                                {...barTickProps}
                                                                fontSize={11}
                                                                tickLine={false}
                                                                axisLine={false}
                                                                tickFormatter={(
                                                                    v,
                                                                ) =>
                                                                    formatThirdPanelY(
                                                                        Number(
                                                                            v,
                                                                        ),
                                                                    )
                                                                }
                                                                width={42}
                                                                domain={
                                                                    chartRows.length
                                                                        ? ([
                                                                              "dataMin",
                                                                              "dataMax",
                                                                          ] as [
                                                                              string,
                                                                              string,
                                                                          ])
                                                                        : undefined
                                                                }
                                                                allowDataOverflow
                                                            />
                                                            <Tooltip
                                                                contentStyle={
                                                                    tooltipContentStyle
                                                                }
                                                                formatter={(
                                                                    v: number,
                                                                ) => [
                                                                    Number(
                                                                        v,
                                                                    ).toLocaleString(
                                                                        undefined,
                                                                        {
                                                                            maximumFractionDigits: 2,
                                                                        },
                                                                    ),
                                                                    thirdPanelSeriesLabel,
                                                                ]}
                                                                labelFormatter={(
                                                                    l,
                                                                ) =>
                                                                    `Strike: ${l}`
                                                                }
                                                            />
                                                            <Legend content={CustomLegend} wrapperStyle={legendWrapperStyle} />
                                                            <Bar
                                                                dataKey={
                                                                    thirdPanelDataKey
                                                                }
                                                                name={
                                                                    thirdPanelSeriesLabel
                                                                }
                                                                fill={
                                                                    spotGammaBarPositive
                                                                }
                                                                radius={[
                                                                    0, 0, 0, 0,
                                                                ]}
                                                                onClick={(
                                                                    data: unknown,
                                                                ) =>
                                                                    onBarClick(
                                                                        data,
                                                                        "CALL",
                                                                    )
                                                                }
                                                                cursor="pointer"
                                                            >
                                                                {(
                                                                    chartRows as typeof byStrike
                                                                ).map(
                                                                    (
                                                                        entry,
                                                                        index,
                                                                    ) => {
                                                                        const val =
                                                                            showGammaExposure
                                                                                ? entry.gammaExposure
                                                                                : entry.spotGammaDisplay;
                                                                        const posFill =
                                                                            showGammaExposure
                                                                                ? dsSuccess
                                                                                : spotGammaBarPositive;
                                                                        return (
                                                                            <Cell
                                                                                key={`sg-${index}`}
                                                                                fill={
                                                                                    (val ??
                                                                                        0) >=
                                                                                    0
                                                                                        ? posFill
                                                                                        : dsError
                                                                                }
                                                                            />
                                                                        );
                                                                    },
                                                                )}
                                                            </Bar>
                                                        </BarChart>
                                                    )}
                                                </ResponsiveContainer>
                                                <p
                                                    className="text-xs mt-1"
                                                    style={{
                                                        color: colors.textMuted,
                                                    }}
                                                >
                                                    {showGammaExposure
                                                        ? language === "ar"
                                                            ? "أسهم لكل حركة 1$"
                                                            : "shares per $ move"
                                                        : language === "ar"
                                                          ? "دولار جاما لكل 1%"
                                                          : "dollar gamma per 1% (index)"}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Fourth panel: Volume Gamma Exposure */}
                            {showVolumeGammaExposure && (
                                <div className="shrink-0 vsg-ds-panel">
                                    <h3
                                        className="mb-1"
                                        style={{ fontSize: 14, fontWeight: 600, color: "var(--txt)" }}
                                    >
                                        {language === "ar"
                                            ? "تعرض جاما على الحجم"
                                            : "Volume Gamma Exposure"}
                                        {mergeVolumeGexWithOI &&
                                            ` (${language === "ar" ? "مدمج مع الفتح المفتوح" : "merged with OI"})`}
                                    </h3>
                                    {!hasVolumeGammaExposureMetricData && !mergeVolumeGexWithOI ? (
                                        <p
                                            className="py-6 text-center text-sm opacity-80"
                                            style={{ color: colors.text }}
                                        >
                                            {missingDataLabel}
                                        </p>
                                    ) : (
                                        <div
                                            style={{
                                                width: scrollWidth ?? "100%",
                                                minWidth: "100%",
                                            }}
                                        >
                                            <ResponsiveContainer
                                                width="100%"
                                                height={panelHeight}
                                            >
                                                    {linearChart ? (
                                                        <LineChart
                                                            data={chartRowsWithMerge as typeof byStrike}
                                                            margin={CHART_MARGIN}
                                                        >
                                                            <CartesianGrid
                                                                strokeDasharray="3 4"
                                                                stroke={gridClr}
                                                                opacity={0.5}
                                                                vertical
                                                                horizontal
                                                            />
                                                            <ReferenceLine
                                                                y={0}
                                                                stroke={strokeClr}
                                                                strokeWidth={1}
                                                            />
                                                            {Number.isFinite(
                                                                underlyingPriceFallback,
                                                            ) &&
                                                                underlyingPriceFallback >
                                                                    0 && (
                                                                    <ReferenceLine
                                                                        x={
                                                                            underlyingPriceFallback
                                                                        }
                                                                        stroke={spotLineClr}
                                                                        strokeDasharray="4 4"
                                                                        strokeOpacity={0.85}
                                                                    />
                                                                )}
                                                            <XAxis {...linearXAxisProps} />
                                                            <YAxis
                                                                stroke={strokeClr}
                                                                fontSize={11}
                                                                tickLine={false}
                                                                axisLine={false}
                                                                tickFormatter={(v) =>
                                                                    formatThirdPanelY(
                                                                        Number(v),
                                                                    )
                                                                }
                                                                width={42}
                                                                domain={
                                                                    chartRows.length
                                                                        ? ([
                                                                              "dataMin",
                                                                              "dataMax",
                                                                          ] as [
                                                                              string,
                                                                              string,
                                                                          ])
                                                                        : undefined
                                                                }
                                                                allowDataOverflow
                                                            />
                                                            <Tooltip
                                                                contentStyle={
                                                                    tooltipContentStyle
                                                                }
                                                                formatter={(v: number) => [
                                                                    Number(v).toLocaleString(
                                                                        undefined,
                                                                        {
                                                                            maximumFractionDigits: 2,
                                                                        },
                                                                    ),
                                                                    language === "ar"
                                                                        ? "تعرض جاما على الحجم"
                                                                        : "Volume gamma exposure",
                                                                ]}
                                                                labelFormatter={(l) =>
                                                                    `Strike: ${l}`
                                                                }
                                                            />
                                                            <Legend
                                                                content={CustomLegend}
                                                                wrapperStyle={legendWrapperStyle}
                                                            />
                                                            <Line
                                                                type="monotone"
                                                                dataKey={
                                                                    mergeVolumeGexWithOI
                                                                        ? "mergedVolumeGex"
                                                                        : "volumeGammaExposure"
                                                                }
                                                                name={
                                                                    language === "ar"
                                                                        ? "تعرض جاما على الحجم"
                                                                        : "Volume gamma exposure"
                                                                }
                                                                stroke={dsAccent}
                                                                strokeWidth={2.5}
                                                                dot={LINE_DOT_INVISIBLE}
                                                                activeDot={{
                                                                    ...LINE_ACTIVE_DOT,
                                                                    fill: dsAccent,
                                                                }}
                                                                onClick={onLinePointClick(
                                                                    "CALL",
                                                                )}
                                                            />
                                                            {mergeVolumeGexWithOI && (
                                                                <Line
                                                                    type="monotone"
                                                                    dataKey="gammaExposure"
                                                                    name={
                                                                        language === "ar"
                                                                            ? "تعرض جاما (على الفتح المفتوح)"
                                                                            : "OI gamma exposure"
                                                                    }
                                                                    stroke={spotGammaLineColor}
                                                                    strokeWidth={2.5}
                                                                    dot={LINE_DOT_INVISIBLE}
                                                                    activeDot={{
                                                                        ...LINE_ACTIVE_DOT,
                                                                        fill: spotGammaLineColor,
                                                                    }}
                                                                    onClick={onLinePointClick(
                                                                        "CALL",
                                                                    )}
                                                                />
                                                            )}
                                                        </LineChart>
                                                    ) : useExpiryStackCharts &&
                                                      stackExpiryList ? (
                                                        <BarChart
                                                            data={
                                                                chartRowsWithMerge as Record<
                                                                    string,
                                                                    unknown
                                                                >[]
                                                            }
                                                            margin={CHART_MARGIN}
                                                        >
                                                            <CartesianGrid
                                                                strokeDasharray="3 4"
                                                                stroke={gridClr}
                                                                opacity={0.5}
                                                                vertical
                                                                horizontal
                                                            />
                                                            <ReferenceLine
                                                                y={0}
                                                                stroke={strokeClr}
                                                                strokeWidth={1}
                                                            />
                                                            {Number.isFinite(
                                                                underlyingPriceFallback,
                                                            ) &&
                                                                underlyingPriceFallback >
                                                                    0 && (
                                                                    <ReferenceLine
                                                                        x={
                                                                            underlyingPriceFallback
                                                                        }
                                                                        stroke={spotLineClr}
                                                                        strokeDasharray="4 4"
                                                                        strokeOpacity={0.85}
                                                                    />
                                                                )}
                                                            <XAxis {...linearXAxisProps} />
                                                            <YAxis
                                                                stroke={strokeClr}
                                                                fontSize={11}
                                                                tickLine={false}
                                                                axisLine={false}
                                                                tickFormatter={(v) =>
                                                                    formatThirdPanelY(
                                                                        Number(v),
                                                                    )
                                                                }
                                                                width={42}
                                                                domain={
                                                                    chartRows.length
                                                                        ? ([
                                                                              "dataMin",
                                                                              "dataMax",
                                                                          ] as [
                                                                              string,
                                                                              string,
                                                                          ])
                                                                        : undefined
                                                                }
                                                                allowDataOverflow
                                                            />
                                                            <Tooltip
                                                                content={stackExpiryTooltip}
                                                            />
                                                            <Legend
                                                                content={CustomLegend}
                                                                wrapperStyle={legendWrapperStyle}
                                                            />
                                                            {stackExpiryList.map(
                                                                (exp, i) => (
                                                                    <Bar
                                                                        key={exp}
                                                                        stackId="volgex"
                                                                        dataKey={`volGex_${expiryLabelToStackBase(exp)}`}
                                                                        name={`${shortenExpiryLegend(exp)} (${language === "ar" ? "حجم" : "Vol"})`}
                                                                        fill={stackPalette[i]}
                                                                        radius={0}
                                                                        onClick={(
                                                                            data: unknown,
                                                                        ) =>
                                                                            onBarClick(
                                                                                data,
                                                                                "CALL",
                                                                            )
                                                                        }
                                                                        cursor="pointer"
                                                                    />
                                                                ),
                                                            )}
                                                            {mergeVolumeGexWithOI &&
                                                                stackExpiryList.map(
                                                                    (exp, i) => (
                                                                        <Bar
                                                                            key={`oi-${exp}`}
                                                                            stackId="oigex"
                                                                            dataKey={`gex_${expiryLabelToStackBase(exp)}`}
                                                                            name={`${shortenExpiryLegend(exp)} (${language === "ar" ? "فتح" : "OI"})`}
                                                                            fill={
                                                                                stackPalette[
                                                                                    i
                                                                                ]
                                                                            }
                                                                            radius={0}
                                                                            opacity={0.5}
                                                                            onClick={(
                                                                                data: unknown,
                                                                            ) =>
                                                                                onBarClick(
                                                                                    data,
                                                                                    "CALL",
                                                                                )
                                                                            }
                                                                            cursor="pointer"
                                                                        />
                                                                    ),
                                                                )}
                                                        </BarChart>
                                                    ) : (
                                                        <BarChart
                                                            data={
                                                                chartRowsWithMerge as typeof byStrike
                                                            }
                                                            margin={CHART_MARGIN}
                                                        >
                                                            <CartesianGrid
                                                                strokeDasharray="3 4"
                                                                stroke={gridClr}
                                                                opacity={0.5}
                                                                vertical
                                                                horizontal
                                                            />
                                                            <ReferenceLine
                                                                y={0}
                                                                stroke={strokeClr}
                                                                strokeWidth={1}
                                                            />
                                                            {Number.isFinite(
                                                                underlyingPriceFallback,
                                                            ) &&
                                                                underlyingPriceFallback >
                                                                    0 && (
                                                                    <ReferenceLine
                                                                        x={
                                                                            underlyingPriceFallback
                                                                        }
                                                                        stroke={spotLineClr}
                                                                        strokeDasharray="4 4"
                                                                        strokeOpacity={0.85}
                                                                    />
                                                                )}
                                                            <XAxis {...linearXAxisProps} />
                                                            <YAxis
                                                                stroke={strokeClr}
                                                                fontSize={11}
                                                                tickLine={false}
                                                                axisLine={false}
                                                                tickFormatter={(v) =>
                                                                    formatThirdPanelY(
                                                                        Number(v),
                                                                    )
                                                                }
                                                                width={42}
                                                                domain={
                                                                    chartRows.length
                                                                        ? ([
                                                                              "dataMin",
                                                                              "dataMax",
                                                                          ] as [
                                                                              string,
                                                                              string,
                                                                          ])
                                                                        : undefined
                                                                }
                                                                allowDataOverflow
                                                            />
                                                            <Tooltip
                                                                contentStyle={
                                                                    tooltipContentStyle
                                                                }
                                                                formatter={(v: number) => [
                                                                    Number(v).toLocaleString(
                                                                        undefined,
                                                                        {
                                                                            maximumFractionDigits: 2,
                                                                        },
                                                                    ),
                                                                    language === "ar"
                                                                        ? "تعرض جاما على الحجم"
                                                                        : "Volume gamma exposure",
                                                                ]}
                                                                labelFormatter={(l) =>
                                                                    `Strike: ${l}`
                                                                }
                                                            />
                                                            <Legend
                                                                content={CustomLegend}
                                                                wrapperStyle={legendWrapperStyle}
                                                            />
                                                            <Bar
                                                                dataKey={
                                                                    mergeVolumeGexWithOI
                                                                        ? "mergedVolumeGex"
                                                                        : "volumeGammaExposure"
                                                                }
                                                                name={
                                                                    language === "ar"
                                                                        ? "تعرض جاما على الحجم"
                                                                        : "Volume gamma exposure"
                                                                }
                                                                radius={[0, 0, 0, 0]}
                                                                onClick={(
                                                                    data: unknown,
                                                                ) =>
                                                                    onBarClick(
                                                                        data,
                                                                        "CALL",
                                                                    )
                                                                }
                                                                cursor="pointer"
                                                            >
                                                                {(
                                                                    chartRows as typeof byStrike
                                                                ).map(
                                                                    (entry, index) => {
                                                                        const val =
                                                                            mergeVolumeGexWithOI
                                                                                ? (entry as unknown as { mergedVolumeGex?: number }).mergedVolumeGex
                                                                                : entry.volumeGammaExposure;
                                                                        return (
                                                                            <Cell
                                                                                key={`vgex-${index}`}
                                                                                fill={
                                                                                    (val ?? 0) >= 0
                                                                                        ? dsSuccess
                                                                                        : dsError
                                                                                }
                                                                            />
                                                                        );
                                                                    },
                                                                )}
                                                            </Bar>
                                                            {mergeVolumeGexWithOI && (
                                                                <Bar
                                                                    dataKey="gammaExposure"
                                                                    name={
                                                                        language === "ar"
                                                                            ? "تعرض جاما (على الفتح المفتوح)"
                                                                            : "OI gamma exposure"
                                                                    }
                                                                    radius={[0, 0, 0, 0]}
                                                                    opacity={0.5}
                                                                    onClick={(
                                                                        data: unknown,
                                                                    ) =>
                                                                        onBarClick(
                                                                            data,
                                                                            "CALL",
                                                                        )
                                                                    }
                                                                    cursor="pointer"
                                                                >
                                                                    {(
                                                                        chartRows as typeof byStrike
                                                                    ).map(
                                                                        (entry, index) => (
                                                                            <Cell
                                                                                key={`oigex-${index}`}
                                                                                fill={
                                                                                    (entry.gammaExposure ?? 0) >= 0
                                                                                        ? dsSuccess
                                                                                        : dsError
                                                                                }
                                                                            />
                                                                        ),
                                                                    )}
                                                                </Bar>
                                                            )}
                                                        </BarChart>
                                                    )}
                                                </ResponsiveContainer>
                                        </div>
                                    )}
                                    <p
                                        className="text-xs mt-1"
                                        style={{ color: colors.textMuted }}
                                    >
                                        {language === "ar"
                                            ? "أسهم لكل حركة 1$ (على الحجم)"
                                            : "shares per $ move (volume-based)"}
                                    </p>
                                </div>
                            )}

                            {!showVolume &&
                                !showOpenInterest &&
                                !showSpotGamma &&
                                !showVolumeGammaExposure &&
                                !mergeVolumeOI && (
                                    <p
                                        className="py-4 text-center text-sm"
                                        style={{ color: colors.text }}
                                    >
                                        {language === "ar"
                                            ? "فعّل حقل واحد على الأقل (حجم، فتح مفتوح، سخيار بيع جاما)"
                                            : "Enable at least one of Volume, Open Interest, Spot Gamma, or Volume Gamma Exposure."}
                                    </p>
                                )}
                        </>
                    )}
                </section>
            </div>
        </>
    );
};

export default VolumeOISpotGammaChart;
